import React, { useEffect, useState, useRef } from "react";
import { db, storage } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  collection, getDocs, doc, setDoc, getDoc,
  updateDoc, deleteDoc, serverTimestamp, query, where, limit,
  orderBy, getCountFromServer, writeBatch, startAfter, increment,
  collectionGroup,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import * as XLSX from "xlsx";
import { isPointInPolygon } from "geolib";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Modal from "../../components/Modal";
import {
  Building2, PlusCircle, Settings, CheckCircle, Truck, ClipboardList,
  AlertTriangle, RefreshCw, Trash2, Lock, KeyRound, Loader2,
  Crown, UserCheck, Map as MapIcon, Package, Save, Image, FolderOpen,
  Upload, Download, Mail, ShieldCheck, CreditCard, Wallet, Calendar, CalendarDays,
  Lightbulb, Clock, BarChart2, MapPin, Info, RotateCcw,
  Zap, Navigation, Code, ShoppingCart, Activity, Users, Globe, Moon, Shield, Monitor, Palette, Database,
} from "lucide-react";
import { useBranding } from "../../context/BrandingContext";

// ── Main Shell ─────────────────────────────────────────────────────────────
function SuperAdminDashboard() {
  const { currentUser, logout } = useAuth();
  const { t } = useLanguage();
  const { branding } = useBranding();
  const [activePage, setActivePage]           = useState("companies");
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Dark mode lives at top level so entire dashboard layout reacts
  const [isDarkMode, setIsDarkMode] = useState(() =>
    localStorage.getItem("pinpoint_dark_mode") === "true"
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);
  function toggleDarkMode() {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    localStorage.setItem("pinpoint_dark_mode", String(newValue));
  }

  function openCompany(company) {
    setSelectedCompany(company);
    setActivePage("company_detail");
  }

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const SA_NAV = [
    { id: "companies",   icon: Building2,   label: t("nav_all_companies")   || "All Companies"   },
    { id: "create",      icon: PlusCircle,  label: t("nav_create_company")  || "Create Company"  },
    { id: "settings",    icon: Settings,    label: t("nav_global_settings") || "Global Settings" },
    { id: "health",      icon: Activity,    label: t("nav_health")          || "Platform Health" },
    { id: "sa_settings", icon: Globe,       label: t("nav_sa_settings")     || "My Settings"     },
  ];
  const activeLabel = SA_NAV.find(n => n.id === activePage || (activePage === "company_detail" && n.id === "companies"))?.label || "Details";

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}>

      {/* Navbar */}
      <nav className="bg-gray-900 text-white sticky top-0 z-40">
        <div className="px-4 md:px-6 py-3 flex justify-between items-center w-full">
          <div className="flex items-center gap-2">
            <button className="lg:hidden p-1.5 rounded-lg text-gray-400 hover:bg-gray-700"
              onClick={() => setSidebarOpen(o => !o)}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d={sidebarOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="w-8 h-8 object-contain rounded-lg" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: branding.primary_color }}>
                <span className="text-white text-xs font-bold">
                  {(branding.platform_name || "P").charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <span className="font-extrabold text-lg tracking-wide text-white">{(branding.platform_name || "Pinpoint").toUpperCase()}</span>
            <span className="text-xs bg-yellow-400 text-gray-900 px-2 py-0.5 rounded-full font-bold">{t("sa_super_admin_badge") || "SUPER ADMIN"}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="lg:hidden text-sm text-gray-300 font-medium">{activeLabel}</span>
            <span className="hidden md:inline text-sm text-gray-300 truncate max-w-[160px]">{currentUser?.email}</span>
            <button onClick={logout} className="text-xs md:text-sm text-red-400 hover:text-red-300 font-medium">{t("logout") || "Logout"}</button>
          </div>
        </div>
      </nav>

      {/* ── LAYOUT: full-width flex row — sidebar sticky left, content fills rest. Do NOT add max-w, mx-auto, gap, or ml- here. ── */}
      <div className="flex flex-1 w-full">

        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed top-[57px] left-0 z-30 h-[calc(100vh-57px)] w-56 shadow-xl overflow-y-auto
          transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          ${isDarkMode ? "bg-gray-800" : "bg-white"}
          lg:sticky lg:top-[57px] lg:translate-x-0 lg:shadow-none lg:h-[calc(100vh-57px)] lg:w-52 lg:shrink-0 lg:overflow-y-auto lg:border-r
          ${isDarkMode ? "lg:border-gray-700" : "lg:border-gray-200"}
        `}>
          <div className="p-3 lg:py-3 lg:px-2">
              <nav className="space-y-1">
                {SA_NAV.map(item => (
                  <button key={item.id} onClick={() => { setActivePage(item.id); setSelectedCompany(null); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
                      activePage === item.id || (activePage === "company_detail" && item.id === "companies")
                        ? "bg-yellow-50 text-yellow-700"
                        : isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-50"
                    }`}>
                    {(() => { const Icon = item.icon; return Icon ? <Icon className="w-4 h-4" /> : null; })()}<span>{item.label}</span>
                  </button>
                ))}
              </nav>
            {selectedCompany && activePage === "company_detail" && (
              <div className={`border rounded-xl p-3 ${isDarkMode ? "bg-gray-700 border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                <p className="text-xs text-yellow-500 font-medium mb-1">{t("sa_viewing") || "Viewing:"}</p>
                <p className={`text-sm font-semibold truncate ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}>{selectedCompany.name || selectedCompany.id}</p>
                <button onClick={() => { setActivePage("companies"); setSelectedCompany(null); setSidebarOpen(false); }}
                  className="text-xs text-yellow-500 hover:text-yellow-400 mt-1">{t("sa_back_all") || "← Back to all"}</button>
              </div>
            )}
          </div>
        </aside>

        {/* Content */}
        <div className={`flex-1 min-w-0 p-3 md:p-6 ${isDarkMode ? "bg-gray-900" : ""}`}>
          {activePage === "companies"      && <CompaniesList onOpen={openCompany} />}
          {activePage === "create"         && <CreateCompany onDone={() => setActivePage("companies")} />}
          {activePage === "settings"       && <GlobalSettings />}
          {activePage === "health"         && (
            <PlatformHealth
              onOpenCompany={(company) => {
                setSelectedCompany(company);
                setActivePage("company_detail");
              }}
            />
          )}
          {activePage === "sa_settings"    && <SuperAdminSettings isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />}
          {activePage === "company_detail" && selectedCompany && (
            <CompanyDetail
              company={selectedCompany}
              onBack={() => { setActivePage("companies"); setSelectedCompany(null); }}
              onUpdate={updated => setSelectedCompany(prev => ({ ...prev, ...updated }))}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function StatCard({ icon, value, label, color }) {
  const colors = {
    blue:   "bg-blue-50 text-blue-600",
    green:  "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    yellow: "bg-yellow-50 text-yellow-600",
    red:    "bg-red-50 text-red-600",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>{(() => { const I = icon; return I ? <I className="w-5 h-5" /> : null; })()}</div>
      <p className={`text-2xl font-bold ${colors[color].split(" ")[1]}`}>{value}</p>
      <p className="text-gray-500 text-sm mt-0.5">{label}</p>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatActivity(ts, t) {
  if (!ts) return t ? t("sa_no_activity") : "No activity";
  const d    = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((new Date() - d) / 86400000);
  if (diff === 0) return t ? t("sa_today")     : "Today";
  if (diff === 1) return t ? t("sa_yesterday") : "Yesterday";
  if (diff < 7)  return `${diff} ${t ? t("sa_days_ago") : "days ago"}`;
  return formatDate(ts);
}

function subscriptionStatus(company) {
  const status = company.subscription_status || "trial";
  const expiry = company.subscription_expiry;
  if (expiry) {
    const d = expiry.toDate ? expiry.toDate() : new Date(expiry);
    if (d < new Date()) return "expired";
  }
  return status;
}

function subBadge(status) {
  const map = {
    paid:    "bg-green-100 text-green-700",
    trial:   "bg-blue-100 text-blue-700",
    unpaid:  "bg-orange-100 text-orange-700",
    expired: "bg-red-100 text-red-700",
  };
  return map[status] || "bg-gray-100 text-gray-500";
}

// ── Companies List ─────────────────────────────────────────────────────────
function CompaniesList({ onOpen }) {
  const { t } = useLanguage();
  const [companies, setCompanies]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState("");
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  useEffect(() => { loadCompanies(); }, []);

  async function loadCompanies() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "companies"));
      // Counter fields are maintained by ClientsManager, VendorsManager, SectionsManager
      // and backfilled by firebase/backfill-counters.js — no subcollection reads needed
      const enriched = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        vendorCount:  d.data().vendor_count  || 0,
        clientCount:  d.data().client_count  || 0,
        sectionCount: d.data().section_count || 0,
        eventCount:   d.data().event_count   || 0,
        lastActivity: d.data().last_activity ?? null,
      }));
      setCompanies(enriched);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function toggleCompany(company) {
    try {
      await updateDoc(doc(db, "companies", company.id), { active: !company.active, updated_at: serverTimestamp() });
      setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, active: !c.active } : c));
      setConfirmToggle(null);
    } catch (err) { console.error(err); }
  }

  async function deleteCompany(company) {
    setDeleting(true);
    try {
      // Delete all subcollections in batches of 200 docs at a time
      const subcollections = ["users", "clients", "sections", "events", "notifications", "deposits", "profile"];
      for (const sub of subcollections) {
        try {
          const snap = await getDocs(collection(db, "companies", company.id, sub));
          if (snap.empty) continue;
          // Delete in chunks of 200 to stay within Firestore limits
          const chunks = [];
          for (let i = 0; i < snap.docs.length; i += 200) {
            chunks.push(snap.docs.slice(i, i + 200));
          }
          for (const chunk of chunks) {
            const promises = chunk.map(d => deleteDoc(doc(db, "companies", company.id, sub, d.id)));
            await Promise.all(promises);
          }
        } catch (subErr) {
          // Skip subcollection errors (e.g. subcollection doesn't exist)
          console.warn(`Could not delete subcollection ${sub}:`, subErr.message);
        }
      }
      // Delete the main company document
      await deleteDoc(doc(db, "companies", company.id));
      setCompanies(prev => prev.filter(c => c.id !== company.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error("Delete company error:", err);
      alert("Error deleting company: " + err.message);
    }
    setDeleting(false);
  }

  const filtered = companies.filter(c =>
    `${c.name} ${c.id}`.toLowerCase().includes(search.toLowerCase())
  );
  const activeCount  = companies.filter(c => c.active !== false).length;
  const totalVendors = companies.reduce((s, c) => s + (c.vendorCount || 0), 0);
  const totalClients = companies.reduce((s, c) => s + (c.clientCount || 0), 0);
  const expiredCount = companies.filter(c => subscriptionStatus(c) === "expired").length;

  if (loading) return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-5 animate-pulse">
            <div className="w-10 h-10 bg-gray-200 rounded-lg mb-3" />
            <div className="h-7 bg-gray-200 rounded w-16 mb-1.5" />
            <div className="h-3.5 bg-gray-100 rounded w-24" />
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="h-5 bg-gray-200 rounded w-40 mb-4" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-4 border-b border-gray-100 last:border-0 animate-pulse">
            <div className="w-12 h-12 bg-gray-200 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-48" />
              <div className="h-3 bg-gray-100 rounded w-32" />
              <div className="flex gap-4">
                <div className="h-3 bg-gray-100 rounded w-20" />
                <div className="h-3 bg-gray-100 rounded w-20" />
                <div className="h-3 bg-gray-100 rounded w-20" />
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <div className="h-7 w-20 bg-gray-100 rounded-lg" />
              <div className="h-7 w-16 bg-gray-100 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={Building2}    value={companies.length} label={t("lbl_total_companies") || "Total Companies"} color="blue" />
        <StatCard icon={CheckCircle}  value={activeCount}       label={t("active")              || "Active"}          color="green" />
        <StatCard icon={Truck}        value={totalVendors}      label={t("lbl_total_vendors")   || "Total Vendors"}   color="purple" />
        <StatCard icon={ClipboardList} value={totalClients}     label={t("lbl_total_clients")   || "Total Clients"}   color="yellow" />
      </div>
      {expiredCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <p className="text-red-700 text-sm font-medium">{expiredCount} company subscription{expiredCount > 1 ? "s" : ""} expired — check billing.</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-800">{t("nav_all_companies") || "All Companies"}</h2>
        <div className="flex gap-2 flex-wrap">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("search") || "Search..."}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 w-full sm:w-48" />
          <button onClick={loadCompanies} className="text-sm text-gray-500 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> {t("btn_refresh") || "Refresh"}</button>
        </div>
      </div>

      {/* ── Disable / Enable Modal ── */}
      <Modal
        isOpen={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title={confirmToggle?.active !== false ? (t("sa_disable_company") || "Disable Company") : (t("sa_enable_company") || "Enable Company")}
        size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${confirmToggle?.active !== false ? "bg-red-100" : "bg-green-100"}`}>
              <span className="text-lg">{confirmToggle?.active !== false ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-green-500" />}</span>
            </div>
            <div>
              <p className="text-gray-800 font-medium text-sm">
                {confirmToggle?.active !== false
                  ? `${t("btn_disable") || "Disable"} "${confirmToggle?.name}"?`
                  : `${t("btn_enable") || "Enable"} "${confirmToggle?.name}"?`}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {confirmToggle?.active !== false
                  ? "All users will lose access immediately. You can re-enable anytime."
                  : "Users will regain full access to their dashboard."}
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={() => toggleCompany(confirmToggle)}
              className={`text-white text-sm font-medium px-5 py-2 rounded-lg transition ${confirmToggle?.active !== false ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}>
              {confirmToggle?.active !== false ? (t("btn_yes_disable") || "Yes, Disable") : (t("btn_yes_enable") || "Yes, Enable")}
            </button>
            <button onClick={() => setConfirmToggle(null)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel") || "Cancel"}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={t("sa_delete_company") || "Delete Company"}
        size="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-lg"><Trash2 className="w-5 h-5 text-red-500" /></span>
            </div>
            <div>
              <p className="text-gray-800 font-medium text-sm">
                {t("delete") || "Delete"} "{confirmDelete?.name || confirmDelete?.id}"?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                This will delete <strong>all data</strong>: {confirmDelete?.vendorCount || 0} {t("sa_vendors_count") || "vendors"},{" "}
                {confirmDelete?.clientCount || 0} {t("sa_clients_count") || "clients"}, {confirmDelete?.eventCount || 0} {t("sa_events_count") || "events"}.
                <br /><span className="text-red-600 font-medium">This cannot be undone.</span>
              </p>
            </div>
          </div>
          <div className="flex gap-3 pt-2 border-t">
            <button onClick={() => deleteCompany(confirmDelete)} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2">
              {deleting ? (t("deleting") || "Deleting...") : <span className="flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> {t("btn_yes_delete_all") || "Yes, Delete All"}</span>}
            </button>
            <button onClick={() => setConfirmDelete(null)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
              {t("cancel") || "Cancel"}
            </button>
          </div>
        </div>
      </Modal>

      <div className="space-y-3">
        {filtered.map(company => {
          const subStatus = subscriptionStatus(company);
          return (
            <div key={company.id} className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div className="flex items-center gap-4">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt="logo" className="w-12 h-12 rounded-xl object-cover border border-gray-200" />
                  ) : (
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl ${company.active !== false ? "bg-blue-500" : "bg-gray-300"}`}>
                      {(company.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-800">{company.name || company.id}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${company.active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                        {company.active !== false ? (t("active") || "Active") : (t("disabled") || "Disabled")}
                      </span>
                      {company.plan && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full capitalize">{company.plan}</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${subBadge(subStatus)}`}>{subStatus}</span>
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5 font-mono">{company.id}</p>
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      <span className="text-xs text-gray-500 flex items-center gap-1"><Truck className="w-3 h-3" /> {company.vendorCount || 0} {t("sa_vendors_count") || "vendors"}</span>
                      <span className="text-xs text-gray-500 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {company.clientCount || 0} {t("sa_clients_count") || "clients"}</span>
                      <span className="text-xs text-gray-500 flex items-center gap-1"><MapIcon className="w-3 h-3" /> {company.sectionCount || 0} {t("sa_zones_count") || "zones"}</span>
                      <span className="text-xs text-gray-500 flex items-center gap-1"><BarChart2 className="w-3 h-3" /> {company.eventCount || 0} {t("sa_events_count") || "events"}</span>
                      <span className={`text-xs flex items-center gap-1 ${company.lastActivity ? "text-green-600" : "text-gray-400"}`}><Clock className="w-3 h-3" /> {formatActivity(company.lastActivity, t)}</span>
                      {company.subscription_expiry && (
                        <span className={`text-xs ${subStatus === "expired" ? "text-red-600 font-medium" : "text-gray-400"}`}>
                          <Calendar className="w-3 h-3 inline mr-1" />{t("sa_expires") || "Expires:"} {formatDate(company.subscription_expiry)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 sm:mt-0">
                  <button onClick={() => onOpen(company)} className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-medium">{t("btn_view_details") || "View Details"}</button>
                  <button onClick={() => setConfirmToggle(company)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium ${company.active !== false ? "bg-orange-50 text-orange-600 hover:bg-orange-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}>
                    {company.active !== false ? (t("btn_disable") || "Disable") : (t("btn_enable") || "Enable")}
                  </button>
                  <button onClick={() => setConfirmDelete(company)} className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium">{t("delete") || "Delete"}</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Company Detail ─────────────────────────────────────────────────────────
function CompanyDetail({ company, onBack, onUpdate }) {
  const { t } = useLanguage();
  const [tab, setTab]             = useState("overview");
  const [users, setUsers]         = useState([]);
  const [clientCount, setClientCount] = useState(0);
  const [sections, setSections]   = useState([]);
  const [loading, setLoading]     = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDetail(); }, [company.id]);

  async function loadDetail() {
    setLoading(true);
    try {
      const [usersSnap, clientsCountSnap, sectionsSnap] = await Promise.all([
        getDocs(collection(db, "companies", company.id, "users")),
        getCountFromServer(collection(db, "companies", company.id, "clients")),
        getDocs(collection(db, "companies", company.id, "sections")),
      ]);
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setClientCount(clientsCountSnap.data().count);
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  const vendors     = users.filter(u => u.role === "vendor");
  const admins      = users.filter(u => u.role === "admin");
  const supervisors = users.filter(u => u.role === "supervisor");
  const subStatus   = subscriptionStatus(company);

  const tabs = [
    { id: "overview",     label: t("tab_overview")                                              || "Overview"      },
    { id: "edit",         label: t("tab_edit")                                                  || "Edit"          },
    { id: "subscription", label: t("tab_subscription")                                          || "Subscription"  },
    { id: "sessions",     label: t("tab_sessions")                                               || "Sessions"       },
    { id: "vendors",      label: `${t("tab_vendors") || "Vendors"} (${vendors.length})`         },
    { id: "clients",      label: `${t("tab_clients") || "Clients"} (${clientCount})`            },
    { id: "zones",        label: `${t("tab_zones") || "Zones"} (${sections.length})`            },
    { id: "reset",        label: t("tab_reset_checkins")                                        || "Reset Check-ins"},
    { id: "analytics",    label: t("tab_analytics")                                             || "Analytics"     },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-gray-400 hover:text-gray-600 text-sm font-medium">{t("sa_back") || "← Back"}</button>
            {company.logo_url ? (
              <img src={company.logo_url} alt="logo" className="w-10 h-10 rounded-xl object-cover border border-gray-200" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-white font-bold">
                {(company.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{company.name || company.id}</h2>
              <p className="text-gray-400 text-xs font-mono">{company.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${company.active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
              {company.active !== false ? (t("active") || "Active") : (t("disabled") || "Disabled")}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${subBadge(subStatus)}`}>{subStatus}</span>
            {company.active === false && (
              <span className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Lock className="w-3 h-3" /> {t("sa_login_blocked") || "Login blocked"}
              </span>
            )}
            <LoginAsAdminButton companyId={company.id} disabled={company.active === false} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b overflow-x-auto">
          {tabs.map(tabItem => (
            <button key={tabItem.id} onClick={() => setTab(tabItem.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition border-b-2 -mb-px ${
                tab === tabItem.id ? "border-yellow-500 text-yellow-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              {tabItem.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center"><p className="text-gray-400 text-sm">{t("sa_loading") || "Loading..."}</p></div>
      ) : (
        <>
          {tab === "overview"     && <OverviewTab company={company} admins={admins} supervisors={supervisors} vendors={vendors} clientCount={clientCount} sections={sections} />}
          {tab === "edit"         && <EditTab company={company} onUpdate={onUpdate} />}
          {tab === "subscription" && <SubscriptionTab company={company} onUpdate={onUpdate} />}
          {tab === "sessions"     && <CompanySessionsTab company={company} />}
          {tab === "vendors"      && <VendorsTab vendors={vendors} />}
          {tab === "clients"      && <ClientsTab company={company} clientCount={clientCount} onReload={loadDetail} />}
          {tab === "zones"        && <ZonesTab company={company} sections={sections} onReload={loadDetail} />}
          {tab === "reset"        && <ResetCheckinsTab company={company} sections={sections} />}
          {tab === "analytics"    && <CompanyAnalytics company={company} />}
        </>
      )}
    </div>
  );
}

// ── Login As Admin Button ──────────────────────────────────────────────────
function LoginAsAdminButton({ companyId, disabled: isDisabled }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  async function handleLogin() {
    setLoading(true);
    setError("");
    try {
      const functions  = getFunctions(undefined, "europe-west1");
      const impersonate = httpsCallable(functions, "impersonateAdmin");
      const result     = await impersonate({ companyId });
      const { customToken } = result.data;

      // Open new tab with the token in the URL hash
      // The admin login page will detect this and sign in automatically
      const url = `${window.location.origin}/admin-login?token=${encodeURIComponent(customToken)}&companyId=${encodeURIComponent(companyId)}`;
      window.open(url, "_blank");
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(""), 5000);
    }
    setLoading(false);
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button onClick={handleLogin} disabled={loading || isDisabled}
        title={isDisabled ? "Company is disabled — enable it first" : ""}
        className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed">
        {loading ? <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> {t("sa_getting_access") || "Getting access..."}</span> : isDisabled ? <span className="flex items-center gap-1.5"><Lock className="w-4 h-4" /> {t("sa_company_disabled") || "Company Disabled"}</span> : <span className="flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> {t("btn_login_as_admin") || "Login as Admin"}</span>}
      </button>
      {error && <p className="text-red-500 text-xs">{error}</p>}
    </div>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────
function OverviewTab({ company, admins, supervisors, vendors, clientCount, sections }) {
  const { t } = useLanguage();
  return (
    <div className="grid grid-cols-3 gap-4">
      {[
        { icon: Crown,        label: t("sa_admins")      || "Admins",      value: admins.length,      color: "yellow" },
        { icon: UserCheck,    label: t("sa_supervisors") || "Supervisors", value: supervisors.length, color: "purple" },
        { icon: Truck,        label: t("tab_vendors")    || "Vendors",     value: vendors.length,     color: "blue"   },
        { icon: ClipboardList,label: t("tab_clients")    || "Clients",     value: company.client_count || clientCount, color: "green"  },
        { icon: MapIcon,      label: t("tab_zones")      || "Zones",       value: sections.length,    color: "blue"   },
        { icon: Package,      label: t("lbl_billing_plan") || "Plan",      value: company.plan || "starter", color: "yellow" },
      ].map((card, i) => (
        <div key={i} className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            {(() => { const I = card.icon; return I ? <I className="w-5 h-5 text-gray-600" /> : null; })()}
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-800">{card.value}</p>
            <p className="text-gray-500 text-sm">{card.label}</p>
          </div>
        </div>
      ))}
      {company.max_vendors && (
        <div className="col-span-3 bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("sa_usage_limits") || "Usage Limits"}</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Vendors</span>
                <span>{vendors.length} / {company.max_vendors}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (vendors.length / company.max_vendors) * 100)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Clients</span>
                <span>{company.client_count || clientCount} / {company.max_clients || "∞"}</span>
              </div>
              {company.max_clients && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((company.client_count || clientCount) / company.max_clients) * 100)}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── UI Switch ──────────────────────────────────────────────────────────────
function UISwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
        checked ? "bg-yellow-500" : "bg-gray-200"
      }`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`} />
    </button>
  );
}

// ── Feature Toggle Row ─────────────────────────────────────────────────────
function FeatureToggleRow({ icon: Icon, label, description, value, onChange }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center mt-0.5">
          {Icon && <Icon className="w-4 h-4 text-gray-600" />}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
      </div>
      <div dir="ltr">
        <UISwitch checked={!!value} onChange={onChange} />
      </div>
    </div>
  );
}

// ── Edit Tab ───────────────────────────────────────────────────────────────
function EditTab({ company, onUpdate }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name:                 company.name || "",
    plan:                 company.plan || "starter",
    max_vendors:          company.max_vendors || 10,
    max_clients:          company.max_clients || 1000,
    out_of_zone_alerts:   company.out_of_zone_alerts !== false,
  });
  const [featureForm, setFeatureForm] = useState({
    stock_feature_enabled:     company.stock_feature_enabled     || false,
    route_optimization:        company.route_optimization        || "disabled",
    anomaly_detection_enabled: company.anomaly_detection_enabled || false,
    api_access_enabled:        company.api_access_enabled        || false,
    api_rate_limit:            company.api_rate_limit            || 60,
  });
  const [savingFeatures, setSavingFeatures] = useState(false);

  // Note: Billing plan is managed in the Subscription tab
  const [adminPassword, setAdminPassword] = useState("");
  const [adminEmail, setAdminEmail]       = useState("");
  const [currentAdminEmail, setCurrentAdminEmail] = useState(t("sa_loading") || "Loading...");
  const [showPass, setShowPass]           = useState(false);
  const [savingEmail, setSavingEmail]     = useState(false);
  const [logoFile, setLogoFile]           = useState(null);
  const [logoPreview, setLogoPreview]     = useState(company.logo_url || null);
  const [saving, setSaving]               = useState(false);
  const [savingLogo, setSavingLogo]       = useState(false);
  const [savingPass, setSavingPass]       = useState(false);
  const [success, setSuccess]             = useState("");
  const [error, setError]                 = useState("");
  const logoInputRef = useRef();

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 4000);
  }

  // Load current admin email on mount
  React.useEffect(() => {
    async function loadAdminEmail() {
      try {
        const snap = await getDocs(
          query(collection(db, "companies", company.id, "users"), where("role", "==", "admin"), limit(1))
        );
        if (!snap.empty) {
          setCurrentAdminEmail(snap.docs[0].data().email || "No email found");
        } else {
          setCurrentAdminEmail("No admin found");
        }
      } catch (err) {
        setCurrentAdminEmail("Could not load");
      }
    }
    loadAdminEmail();
  }, [company.id]);

  async function handleSaveDetails(e) {
    e.preventDefault();
    if (!form.name.trim()) return flash("Company name is required.", true);
    setSaving(true);
    try {
      await updateDoc(doc(db, "companies", company.id), {
        name:                 form.name.trim(),
        plan:                 form.plan,
        max_vendors:          parseInt(form.max_vendors),
        max_clients:          parseInt(form.max_clients),
        out_of_zone_alerts:   form.out_of_zone_alerts,
        updated_at:           serverTimestamp(),
      });
      onUpdate({ name: form.name.trim(), plan: form.plan, max_vendors: parseInt(form.max_vendors), max_clients: parseInt(form.max_clients) });
      flash("✅ Company details saved!");
    } catch (err) { flash(err.message, true); }
    setSaving(false);
  }

  async function handleLogoUpload() {
    if (!logoFile) return;
    setSavingLogo(true);
    try {
      const fileRef = storageRef(storage, `companies/${company.id}/logo`);
      await uploadBytes(fileRef, logoFile);
      const url = await getDownloadURL(fileRef);
      await Promise.all([
        updateDoc(doc(db, "companies", company.id), { logo_url: url, updated_at: serverTimestamp() }),
        setDoc(doc(db, "companies", company.id, "profile", "info"), { logo_url: url }, { merge: true }),
      ]);
      onUpdate({ logo_url: url });
      setLogoPreview(url);
      flash("✅ Logo uploaded!");
    } catch (err) { flash(err.message, true); }
    setSavingLogo(false);
  }

  async function handleResetPassword() {
    if (!adminPassword.trim()) return flash("Enter a new password.", true);
    if (adminPassword.length < 6) return flash("Password must be at least 6 characters.", true);
    setSavingPass(true);
    try {
      // Find admin user document
      const usersSnap = await getDocs(
        query(collection(db, "companies", company.id, "users"), where("role", "==", "admin"), limit(1))
      );
      if (usersSnap.empty) throw new Error("No admin user found for this company.");
      // Write password — Cloud Function (editAccount) will update Firebase Auth and delete it
      await updateDoc(usersSnap.docs[0].ref, { password: adminPassword.trim(), updated_at: serverTimestamp() });
      setAdminPassword("");
      flash("✅ Password reset! Auth account will update automatically.");
    } catch (err) { flash(err.message, true); }
    setSavingPass(false);
  }

  async function handleChangeEmail() {
    if (!adminEmail.trim()) return flash("Enter a new email address.", true);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminEmail.trim())) return flash("Enter a valid email address.", true);
    setSavingEmail(true);
    try {
      const usersSnap = await getDocs(
        query(collection(db, "companies", company.id, "users"), where("role", "==", "admin"), limit(1))
      );
      if (usersSnap.empty) throw new Error("No admin user found for this company.");
      // Write new_email — Cloud Function (editAccount) will update Firebase Auth and delete it
      await updateDoc(usersSnap.docs[0].ref, {
        new_email:  adminEmail.trim(),
        updated_at: serverTimestamp(),
      });
      setAdminEmail("");
      flash("✅ Email change queued! Auth account will update automatically.");
    } catch (err) { flash(err.message, true); }
    setSavingEmail(false);
  }

  async function handleSaveFeatures() {
    setSavingFeatures(true);
    try {
      await updateDoc(doc(db, "companies", company.id), {
        stock_feature_enabled:     featureForm.stock_feature_enabled,
        route_optimization:        featureForm.route_optimization,
        anomaly_detection_enabled: featureForm.anomaly_detection_enabled,
        api_access_enabled:        featureForm.api_access_enabled,
        api_rate_limit:            featureForm.api_rate_limit,
        updated_at:                serverTimestamp(),
      });
      onUpdate({
        stock_feature_enabled:     featureForm.stock_feature_enabled,
        route_optimization:        featureForm.route_optimization,
        anomaly_detection_enabled: featureForm.anomaly_detection_enabled,
        api_access_enabled:        featureForm.api_access_enabled,
        api_rate_limit:            featureForm.api_rate_limit,
      });
      flash("✅ Feature settings saved!");
    } catch (err) {
      flash(`Error: ${err.message}${err.code ? ` (${err.code})` : ""}`, true);
    }
    setSavingFeatures(false);
  }

  return (
    <div className="space-y-4">
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* Company Details */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2"><Building2 className="w-4 h-4" /> {t("lbl_company_details") || "Company Details"}</h3>
        <form onSubmit={handleSaveDetails} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_company_name") || "Company Name"} *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_tier") || "Tier"}</label>
              <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_out_of_zone_alerts") || "Out-of-Zone Alerts"}</label>
              <select value={form.out_of_zone_alerts}
                onChange={e => setForm(p => ({ ...p, out_of_zone_alerts: e.target.value === "true" }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="true">{t("enabled") || "Enabled"}</option>
                <option value="false">{t("disabled") || "Disabled"}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_max_vendors") || "Max Vendors"}</label>
              <input type="number" min={1} value={form.max_vendors}
                onChange={e => setForm(p => ({ ...p, max_vendors: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_max_clients") || "Max Clients"}</label>
              <input type="number" min={1} value={form.max_clients}
                onChange={e => setForm(p => ({ ...p, max_clients: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
            {saving ? (t("lbl_saving") || "Saving...") : <span className="flex items-center gap-1.5"><Save className="w-4 h-4" /> {t("btn_save_details") || "Save Details"}</span>}
          </button>
        </form>
      </div>

      {/* Logo Upload */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2"><Image className="w-4 h-4" /> {t("lbl_company_logo") || "Company Logo"}</h3>
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
            {logoPreview
              ? <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
              : <Building2 className="w-8 h-8 text-gray-300" />
            }
          </div>
          <div className="space-y-2">
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
              onChange={e => {
                const file = e.target.files[0];
                if (file) {
                  setLogoFile(file);
                  setLogoPreview(URL.createObjectURL(file));
                }
              }} />
            <button onClick={() => logoInputRef.current.click()}
              className="block text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium flex items-center gap-1.5">
              <FolderOpen className="w-4 h-4" /> {t("lbl_choose_image") || "Choose Image"}
            </button>
            {logoFile && (
              <button onClick={handleLogoUpload} disabled={savingLogo}
                className="block text-sm bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 flex items-center gap-1.5">
                {savingLogo ? (t("lbl_saving") || "Uploading...") : <><Upload className="w-4 h-4" /> {t("lbl_upload_logo") || "Upload Logo"}</>}
              </button>
            )}
            <p className="text-xs text-gray-400">PNG, JPG — shown in dashboard header, mobile app, and reports</p>
          </div>
        </div>
      </div>

      {/* Feature Controls */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
          <Zap className="w-4 h-4" /> {t("lbl_feature_controls") || "Feature Controls"}
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          {t("lbl_feature_controls_desc") || "Enable or disable features for this company. Changes take effect immediately."}
        </p>

        <div className="space-y-1">

          <FeatureToggleRow
            icon={ShoppingCart}
            label={t("lbl_stock_pos") || "Stock & POS System"}
            description={t("lbl_stock_pos_desc") || "Vendors can manage stock and sell items to clients"}
            value={featureForm.stock_feature_enabled}
            onChange={v => setFeatureForm(p => ({ ...p, stock_feature_enabled: v }))}
          />

          {/* Route Optimization — inline sub-options */}
          <div className="flex items-start justify-between py-3 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mt-0.5">
                <Navigation className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{t("lbl_route_optimization") || "Route Optimization"}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t("lbl_route_optimization_desc") || "Vendors can optimize their daily route automatically"}</p>
                {featureForm.route_optimization !== "disabled" && (
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setFeatureForm(p => ({ ...p, route_optimization: "basic" }))}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                        featureForm.route_optimization === "basic"
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                      {t("lbl_route_basic") || "Basic (Free)"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFeatureForm(p => ({ ...p, route_optimization: "google" }))}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                        featureForm.route_optimization === "google"
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                      {t("lbl_route_google") || "Google Routes (Premium)"}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div dir="ltr">
              <UISwitch
                checked={featureForm.route_optimization !== "disabled"}
                onChange={v => setFeatureForm(p => ({
                  ...p,
                  route_optimization: v ? "basic" : "disabled"
                }))}
              />
            </div>
          </div>

          <FeatureToggleRow
            icon={AlertTriangle}
            label={t("lbl_anomaly_detection") || "Anomaly Detection"}
            description={t("lbl_anomaly_detection_desc") || "Auto-detect suspicious patterns: speeding, long stops, no visits, outside hours"}
            value={featureForm.anomaly_detection_enabled}
            onChange={v => setFeatureForm(p => ({ ...p, anomaly_detection_enabled: v }))}
          />

          <FeatureToggleRow
            icon={Code}
            label={t("lbl_api_access") || "API Access"}
            description={t("lbl_api_access_desc") || "Company admin can generate API keys to integrate with external systems"}
            value={featureForm.api_access_enabled}
            onChange={v => setFeatureForm(p => ({ ...p, api_access_enabled: v }))}
          />

          {featureForm.api_access_enabled && (
            <div className="mt-3 ps-4 border-s-2 border-blue-200">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("lbl_api_rate_limit") || "API Rate Limit (requests per minute)"}
              </label>
              <select
                value={featureForm.api_rate_limit || 60}
                onChange={e => setFeatureForm(p => ({ ...p, api_rate_limit: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value={30}>30 {t("opt_per_minute") || "requests / minute"} — Basic</option>
                <option value={60}>60 {t("opt_per_minute") || "requests / minute"} — Standard</option>
                <option value={120}>120 {t("opt_per_minute") || "requests / minute"} — Professional</option>
                <option value={300}>300 {t("opt_per_minute") || "requests / minute"} — Enterprise</option>
                <option value={0}>0 — {t("opt_unlimited") || "Unlimited"}</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {t("lbl_rate_limit_desc") || "Maximum API requests per minute. Excess requests return 429 error."}
              </p>
            </div>
          )}

        </div>

        <button
          type="button"
          onClick={handleSaveFeatures}
          disabled={savingFeatures}
          className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
          {savingFeatures ? (t("lbl_saving") || "Saving...") : <><Save className="w-4 h-4" /> {t("btn_save_feature_settings") || "Save Feature Settings"}</>}
        </button>
      </div>

      {/* Change Admin Email */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2"><Mail className="w-4 h-4" /> {t("lbl_change_admin_email") || "Change Admin Email"}</h3>
        <p className="text-gray-500 text-sm mb-1">{t("lbl_change_email_desc") || "Changes the login email for this company's admin account."}</p>
        <p className="text-xs text-gray-400 mb-3">
          {t("lbl_current_email") || "Current email:"} <span className="font-medium text-gray-600">{currentAdminEmail}</span>
        </p>
        <div className="flex gap-3">
          <input
            type="email"
            value={adminEmail}
            onChange={e => setAdminEmail(e.target.value)}
            placeholder={t("lbl_admin_email") || "New email address"}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          <button onClick={handleChangeEmail} disabled={savingEmail}
            className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {savingEmail ? (t("lbl_saving") || "Saving...") : <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" /> {t("btn_change_email") || "Change Email"}</span>}
          </button>
        </div>
      </div>

      {/* Reset Admin Password */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {t("lbl_reset_admin_password") || "Reset Admin Password"}</h3>
        <p className="text-gray-500 text-sm mb-3">{t("lbl_reset_password_desc") || "Changes the login password for this company's admin account."}</p>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <input
              type={showPass ? "text" : "password"}
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              placeholder={t("lbl_min_6_chars") || "New password (min 6 characters)"}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 pr-16" />
            <button type="button" onClick={() => setShowPass(p => !p)}
              className="absolute right-2 top-2 text-xs text-gray-400 hover:text-gray-600 px-1">
              {showPass ? (t("form_hide") || "Hide") : (t("form_show") || "Show")}
            </button>
          </div>
          <button onClick={handleResetPassword} disabled={savingPass}
            className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {savingPass ? (t("lbl_resetting") || "Resetting...") : <span className="flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> {t("btn_reset_password") || "Reset"}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Subscription Tab ───────────────────────────────────────────────────────
function SubscriptionTab({ company, onUpdate }) {
  const { t } = useLanguage();
  const toInputDate = ts => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toISOString().split("T")[0];
  };

  const billingPlan = company.billing_plan || "expiry_date"; // monthly | forever | expiry_date
  const [subTab, setSubTab]   = useState("plan");             // plan | deposits | history
  const [planType, setPlanType] = useState(billingPlan);

  // ── Monthly plan state ──
  const [monthly, setMonthly] = useState({
    deposit:         company.billing_deposit       || 0,
    monthly_fee:     company.billing_monthly_fee   || 0,
    current_balance: company.billing_balance       || 0,
    notes:           company.subscription_notes    || "",
  });

  // ── Expiry date plan state ──
  const [expiryForm, setExpiryForm] = useState({
    subscription_expiry: toInputDate(company.subscription_expiry),
    notes:               company.subscription_notes || "",
  });

  // ── Forever plan state ──
  const [foreverNotes, setForeverNotes] = useState(company.subscription_notes || "");

  // ── Deposit form ──
  const [depositAmount, setDepositAmount] = useState("");
  const [depositNote,   setDepositNote]   = useState("");
  const [deposits,      setDeposits]      = useState([]);
  const [loadingDeps,   setLoadingDeps]   = useState(false);

  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError]     = useState("");

  const subStatus = subscriptionStatus(company);
  const isExpired = subStatus === "expired";

  // Load deposit history
  React.useEffect(() => {
    loadDeposits();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id]);

  async function loadDeposits() {
    setLoadingDeps(true);
    try {
      const snap = await getDocs(
        collection(db, "companies", company.id, "deposits")
      );
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.created_at?.toDate ? a.created_at.toDate() : new Date(0);
        const tb = b.created_at?.toDate ? b.created_at.toDate() : new Date(0);
        return tb - ta;
      });
      setDeposits(list);
    } catch (err) { console.error(err); }
    setLoadingDeps(false);
  }

  async function addDeposit() {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) return setError("Enter a valid deposit amount.");
    setError("");
    setSaving(true);
    try {
      // Use current balance from local state (already loaded when company was opened)
      const currentBal = parseFloat(company.billing_balance) || 0;
      const newBal     = currentBal + amount;

      // Step 1: Update company balance + re-activate if suspended
      await updateDoc(doc(db, "companies", company.id), {
        billing_balance: newBal,
        active:          true,
        updated_at:      serverTimestamp(),
      });

      // Step 2: Write deposit record to subcollection
      const depId = "dep-" + Date.now().toString(36);
      await setDoc(doc(db, "companies", company.id, "deposits", depId), {
        amount,
        note:           depositNote.trim() || "",
        type:           "deposit",
        balance_before: currentBal,
        balance_after:  newBal,
        created_at:     serverTimestamp(),
        created_by:     "super_admin",
      });

      // Step 3: Update local state so UI reflects new balance immediately
      onUpdate({ billing_balance: newBal, active: true });
      setDepositAmount("");
      setDepositNote("");
      setSuccess(`✅ Deposit of ${amount} AED added. New balance: ${newBal} AED`);
      setTimeout(() => setSuccess(""), 4000);
      loadDeposits();
    } catch (err) {
      // Show full error so you can debug exactly what is failing
      setError(`Error: ${err.message} (code: ${err.code || "none"})`);
    }
    setSaving(false);
  }

  async function savePlan() {
    setSaving(true);
    setError("");
    try {
      const update = {
        billing_plan: planType,
        updated_at:   serverTimestamp(),
      };

      if (planType === "monthly") {
        update.billing_deposit     = parseFloat(monthly.deposit)     || 0;
        update.billing_monthly_fee = parseFloat(monthly.monthly_fee) || 0;
        update.billing_balance     = parseFloat(monthly.current_balance) || 0;
        update.subscription_notes  = monthly.notes;
        update.subscription_status = update.billing_balance > 0 ? "paid" : "unpaid";
        update.active              = update.billing_balance > 0;
        update.subscription_expiry = null;
      } else if (planType === "forever") {
        update.subscription_notes  = foreverNotes;
        update.subscription_status = "paid";
        update.active              = true;
        update.subscription_expiry = null;
        update.billing_balance     = null;
      } else if (planType === "expiry_date") {
        if (!expiryForm.subscription_expiry) {
          setError("Please set an expiry date.");
          setSaving(false);
          return;
        }
        const expDate = new Date(expiryForm.subscription_expiry);
        update.subscription_expiry = expDate;
        update.subscription_notes  = expiryForm.notes;
        update.subscription_status = expDate > new Date() ? "paid" : "unpaid";
        update.active              = expDate > new Date();
        update.block_on_expiry     = true;
      }

      await updateDoc(doc(db, "companies", company.id), update);
      onUpdate(update);
      setSuccess("✅ Plan saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  const PLAN_OPTIONS = [
    {
      id:    "monthly",
      icon:  Calendar,
      title: t("lbl_monthly_billing") || "Monthly Billing",
      desc:  t("lbl_monthly_billing_desc") || "Client pays a deposit. System deducts monthly fee automatically. Account blocked when balance reaches 0.",
    },
    {
      id:    "forever",
      icon:  Package,
      title: t("lbl_active_forever") || "Active Forever",
      desc:  t("lbl_active_forever_desc") || "No payment tracking. Account stays active until you manually disable or delete it.",
    },
    {
      id:    "expiry_date",
      icon:  CalendarDays,
      title: t("lbl_expiry_by_date") || "Expiry by Date",
      desc:  t("lbl_expiry_by_date_desc") || "Account is active until a set date. Automatically disabled when date passes.",
    },
  ];

  return (
    <div className="space-y-4">

      {/* Status banner */}
      {company.active === false && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4 flex items-center gap-3">
          <Lock className="w-6 h-6 text-red-600" />
          <div>
            <p className="text-red-700 font-semibold text-sm">{t("lbl_account_suspended") || "Account Suspended"}</p>
            <p className="text-red-600 text-xs mt-0.5">
              {billingPlan === "monthly"
                ? (t("lbl_balance_zero_suspended") || "Balance reached 0. Add a deposit to re-activate.")
                : (t("lbl_company_disabled_msg") || "This company is disabled. Re-enable it manually.")}
            </p>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="bg-white rounded-xl shadow-sm p-1 flex gap-1">
        {[
          { id: "plan",     Icon: CreditCard,    text: t("lbl_plan_settings") || "Plan & Settings" },
          { id: "deposits", Icon: Wallet,        text: t("lbl_add_deposit_tab") || "Add Deposit"     },
          { id: "history",  Icon: ClipboardList, text: t("lbl_deposit_history") || "Deposit History" },
        ].map(stab => (
          <button key={stab.id} onClick={() => setSubTab(stab.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5 ${
              subTab === stab.id ? "bg-yellow-500 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}>
            <stab.Icon className="w-4 h-4" />{stab.text}
          </button>
        ))}
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* ── Plan & Settings Tab ── */}
      {subTab === "plan" && (
        <div className="space-y-4">

          {/* Plan selector */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2"><CreditCard className="w-4 h-4" /> {t("lbl_subscription_plan") || "Subscription Plan"}</h3>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {PLAN_OPTIONS.map(plan => (
                <button key={plan.id} type="button" onClick={() => setPlanType(plan.id)}
                  className={`p-4 rounded-xl border-2 text-start transition ${
                    planType === plan.id
                      ? "border-yellow-500 bg-yellow-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}>
                  {(() => { const Icon = plan.icon; return Icon ? <Icon className="w-6 h-6 mb-2" /> : null; })()}
                  <p className="text-sm font-semibold text-gray-800">{plan.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{plan.desc}</p>
                </button>
              ))}
            </div>

            {/* Monthly plan fields */}
            {planType === "monthly" && (
              <div className="space-y-4 border-t pt-4">
                <p className="text-xs text-blue-600 bg-blue-50 rounded-lg p-3 flex items-start gap-2">
                  <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> The system deducts <strong>monthly fee ÷ 30</strong> from the balance every day automatically.
                  When balance reaches 0, the account is suspended and the admin sees a payment message.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_initial_deposit_aed") || "Initial Deposit (AED)"}</label>
                    <input type="number" min={0} value={monthly.deposit}
                      onChange={e => setMonthly(p => ({ ...p, deposit: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="e.g. 500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_monthly_fee_aed") || "Monthly Fee (AED)"}</label>
                    <input type="number" min={0} value={monthly.monthly_fee}
                      onChange={e => setMonthly(p => ({ ...p, monthly_fee: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="e.g. 100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_current_balance_aed") || "Current Balance (AED)"}</label>
                    <input type="number" min={0} value={monthly.current_balance}
                      onChange={e => setMonthly(p => ({ ...p, current_balance: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="e.g. 400" />
                    <p className="text-xs text-gray-400 mt-1">
                      {t("lbl_days_remaining_label") || "Days Remaining"}: {monthly.monthly_fee > 0 ? Math.floor(monthly.current_balance / (monthly.monthly_fee / 30)) : "∞"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_notes") || "Notes"}</label>
                    <input value={monthly.notes}
                      onChange={e => setMonthly(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Internal notes..." />
                  </div>
                </div>
              </div>
            )}

            {/* Forever plan */}
            {planType === "forever" && (
              <div className="border-t pt-4">
                <p className="text-xs text-green-600 bg-green-50 rounded-lg p-3 mb-3 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 flex-shrink-0" /> This company will stay active forever. No balance or expiry tracking needed.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={foreverNotes} onChange={e => setForeverNotes(e.target.value)} rows={2}
                  placeholder="e.g. Special agreement, partner account..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              </div>
            )}

            {/* Expiry date plan */}
            {planType === "expiry_date" && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-xs text-purple-600 bg-purple-50 rounded-lg p-3 flex items-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" /> Account will be automatically disabled when the expiry date passes.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_expiry_date_req") || "Expiry Date"} *</label>
                    <input type="date" value={expiryForm.subscription_expiry}
                      onChange={e => setExpiryForm(p => ({ ...p, subscription_expiry: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_notes") || "Notes"}</label>
                    <input value={expiryForm.notes}
                      onChange={e => setExpiryForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Internal notes..." />
                  </div>
                </div>
              </div>
            )}

            <button onClick={savePlan} disabled={saving}
              className="mt-5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
              {saving ? (t("lbl_saving") || "Saving...") : <span className="flex items-center gap-1.5"><Save className="w-4 h-4" /> {t("btn_save_plan") || "Save Plan"}</span>}
            </button>
          </div>

          {/* Current status summary */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><BarChart2 className="w-4 h-4" /> {t("lbl_current_status") || "Current Status"}</h3>
            <div className="space-y-0 divide-y divide-gray-100 text-sm">
              {[
                { label: t("lbl_plan_type_label") || "Plan Type", value: PLAN_OPTIONS.find(p => p.id === billingPlan)?.title || billingPlan },
                { label: t("lbl_status") || "Status",          value: subStatus,        badge: subBadge(subStatus) },
                { label: t("active") || "Active",              value: company.active !== false ? (t("yes") || "Yes") : (t("no_blocked") || "No — blocked"),
                  valueClass: company.active !== false ? "text-green-600" : "text-red-600" },
                ...(billingPlan === "monthly" ? [
                  { label: t("lbl_monthly_fee_label") || "Monthly Fee",     value: `${company.billing_monthly_fee || 0} AED / mo (${parseFloat((company.billing_monthly_fee || 0) / 30).toFixed(2)} AED / day)` },
                  { label: t("lbl_current_balance_display") || "Current Balance", value: `${parseFloat(company.billing_balance || 0).toFixed(2)} AED`,
                    valueClass: (company.billing_balance || 0) <= 0 ? "text-red-600 font-bold" : "text-green-600 font-bold" },
                  { label: t("lbl_days_remaining_label") || "Days Remaining", value: company.billing_monthly_fee > 0 ? `≈ ${Math.floor((company.billing_balance || 0) / ((company.billing_monthly_fee || 0) / 30))} ${t("lbl_days") || "days"}` : "∞" },
                ] : []),
                ...(billingPlan === "expiry_date" ? [
                  { label: t("sa_expires") || "Expiry Date",   value: formatDate(company.subscription_expiry),
                    valueClass: isExpired ? "text-red-600 font-medium" : "text-gray-800" },
                ] : []),
              ].map((row, i) => (
                <div key={i} className="flex justify-between py-2.5">
                  <span className="text-gray-500">{row.label}</span>
                  {row.badge
                    ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${row.badge}`}>{row.value}</span>
                    : <span className={`font-medium ${row.valueClass || "text-gray-800"}`}>{row.value}</span>
                  }
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Add Deposit Tab ── */}
      {subTab === "deposits" && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 pb-2 border-b flex items-center gap-2"><Wallet className="w-4 h-4" /> {t("lbl_add_deposit_tab") || "Add Deposit"}</h3>
          <p className="text-gray-500 text-sm">
            {t("lbl_current_balance_display") || "Current Balance"}: <strong className={`${(company.billing_balance || 0) <= 0 ? "text-red-600" : "text-green-600"}`}>
              {company.billing_balance || 0} AED
            </strong>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_deposit_amount_aed") || "Deposit Amount (AED)"} *</label>
              <input type="number" min={1} value={depositAmount}
                onChange={e => setDepositAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="e.g. 500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_note_optional") || "Note (optional)"}</label>
              <input value={depositNote} onChange={e => setDepositNote(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="e.g. Bank transfer ref #123" />
            </div>
          </div>
          {depositAmount && parseFloat(depositAmount) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              {t("lbl_new_balance_after") || "New balance after deposit"}: <strong>{(parseFloat(company.billing_balance) || 0) + parseFloat(depositAmount)} AED</strong>
              {company.billing_monthly_fee > 0 && (
                <span className="ml-2 text-green-600">
                  ≈ {Math.floor(((parseFloat(company.billing_balance) || 0) + parseFloat(depositAmount)) / (company.billing_monthly_fee / 30))} days
                </span>
              )}
            </div>
          )}
          <button onClick={addDeposit} disabled={saving || !depositAmount}
            className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
            {saving ? (t("lbl_adding") || "Adding...") : <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4" /> {t("btn_add_deposit") || "Add Deposit"}</span>}
          </button>
        </div>
      )}

      {/* ── Deposit History Tab ── */}
      {subTab === "history" && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2"><ClipboardList className="w-4 h-4" /> {t("lbl_deposit_history") || "Deposit History"}</h3>
          {loadingDeps ? (
            <p className="text-gray-400 text-sm text-center py-6">{t("sa_loading") || "Loading..."}</p>
          ) : deposits.length === 0 ? (
            <div className="text-center py-10">
              <CreditCard className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{t("lbl_no_deposits") || "No deposits recorded yet"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-start py-2 px-3 text-xs font-semibold text-gray-500">{t("th_date") || "Date"}</th>
                    <th className="text-start py-2 px-3 text-xs font-semibold text-gray-500">{t("th_amount") || "Amount"}</th>
                    <th className="text-start py-2 px-3 text-xs font-semibold text-gray-500">{t("th_balance_after") || "Balance After"}</th>
                    <th className="text-start py-2 px-3 text-xs font-semibold text-gray-500">{t("th_type") || "Type"}</th>
                    <th className="text-start py-2 px-3 text-xs font-semibold text-gray-500">{t("th_note") || "Note"}</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map(dep => (
                    <tr key={dep.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2.5 px-3 text-gray-500 text-xs">{formatDate(dep.created_at)}</td>
                      <td className="py-2.5 px-3">
                        <span className={`font-semibold ${dep.type === "deduction" ? "text-red-600" : "text-green-600"}`}>
                          {dep.type === "deduction" ? "-" : "+"}{dep.amount} AED
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-700">{dep.balance_after} AED</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          dep.type === "deduction"
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                        }`}>
                          {dep.type === "deduction" ? (t("lbl_monthly_charge") || "Monthly charge") : (t("lbl_deposit_label") || "Deposit")}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{dep.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Vendors Tab ────────────────────────────────────────────────────────────
function VendorsTab({ vendors }) {
  const { t } = useLanguage();
  return (
    <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
      {vendors.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">{t("lbl_no_vendors_yet") || "No vendors yet"}</div>
      ) : vendors.map(v => (
        <div key={v.id} className="p-4 flex items-center gap-4">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold ${v.active !== false ? "bg-green-500" : "bg-gray-400"}`}>
            {(v.first_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">{v.first_name} {v.last_name}</p>
            <div className="flex flex-wrap gap-3 mt-0.5">
              {v.email && <p className="text-xs text-gray-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {v.email}</p>}
              {v.plate && <p className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 rounded flex items-center gap-1"><Truck className="w-3 h-3" /> {v.plate}</p>}
              {v.imei  && <p className="text-xs text-gray-400 flex items-center gap-1"><Settings className="w-3 h-3" /> {v.imei}</p>}
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${v.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
            {v.active !== false ? (t("active") || "Active") : (t("inactive") || "Inactive")}
          </span>
          {v.uid && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">✓ Auth</span>}
        </div>
      ))}
    </div>
  );
}

// ── Clients Tab ────────────────────────────────────────────────────────────
function ClientsTab({ company, clientCount, onReload }) {
  const { t } = useLanguage();
  const [uploading, setUploading]         = useState(false);
  const [downloading, setDownloading]     = useState(false);
  const [uploadMsg, setUploadMsg]         = useState("");
  const [uploadError, setUploadError]     = useState("");
  const [deleteAllModal, setDeleteAllModal] = useState(false);
  const [deletingAll, setDeletingAll]     = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null); // { unique, duplicates, noCoords, pendingRows }
  const [assigningZones,  setAssigningZones]  = useState(false);
  const [assignProgress,  setAssignProgress]  = useState(0);
  const [assignResult,    setAssignResult]    = useState(null); // { assigned, skipped }
  const fileInputRef = useRef();

  function flash(msg, isError = false) {
    if (isError) setUploadError(msg); else setUploadMsg(msg);
    setTimeout(() => { setUploadMsg(""); setUploadError(""); }, 5000);
  }

  async function handleDeleteAll() {
    setDeletingAll(true);
    try {
      let deleted = 0;
      // Delete in pages of 500 until collection is empty
      while (true) {
        const snap = await getDocs(
          query(collection(db, "companies", company.id, "clients"), limit(500))
        );
        if (snap.empty) break;
        const batch = writeBatch(db);
        snap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
        deleted += snap.docs.size;
      }
      await updateDoc(doc(db, "companies", company.id), { client_count: 0 });
      flash(`✅ Deleted all ${deleted} clients successfully.`);
      onReload();
    } catch (err) {
      flash(`Error: ${err.message}`, true);
    }
    setDeletingAll(false);
    setDeleteAllModal(false);
  }

  // GPS haversine duplicate check — within 10 metres = duplicate
  function isSameLocation(lat1, lng1, lat2, lng2) {
    if (!lat1 || !lng1 || !lat2 || !lng2) return false;
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return distance <= 10;
  }

  // ── Parse file + GPS duplicate check → show preview (no Firestore writes yet) ──
  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    setUploadMsg("");
    setUploadError("");
    setUploadPreview(null);

    try {
      let rows = [];
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith(".kml")) {
        rows = await parseKML(file);
      } else if (fileName.endsWith(".csv")) {
        const text  = await file.text();
        const wb    = XLSX.read(text, { type: "string" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw   = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        console.log("[CSV] Raw headers:", raw.length > 0 ? Object.keys(raw[0]) : "empty");
        rows = raw.map(normalizeRow).filter(r => r.first_name || r.phone);
      } else if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
        const data  = await file.arrayBuffer();
        const wb    = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw   = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        console.log("[EXCEL] Raw headers:", raw.length > 0 ? Object.keys(raw[0]) : "empty");
        rows = raw.map(normalizeRow).filter(r => r.first_name || r.phone);
      } else {
        throw new Error("Unsupported file format. Please use .xlsx, .xls, .csv, or .kml");
      }

      if (rows.length === 0) {
        flash("No valid client rows found in the file.", true);
        setUploading(false);
        return;
      }

      // Fetch all existing client coords from Firestore for GPS duplicate check
      const existingSnap = await getDocs(
        query(collection(db, "companies", company.id, "clients"), limit(5000))
      );
      const existingCoords = existingSnap.docs
        .map(d => {
          const data = d.data();
          return {
            lat: parseFloat(data.location?.lat ?? data.lat ?? 0) || null,
            lng: parseFloat(data.location?.lng ?? data.lng ?? 0) || null,
          };
        })
        .filter(c => c.lat && c.lng);

      // GPS dedup — within file + against existing Firestore clients
      const uniqueRows  = [];
      const seenInFile  = [];
      let   dupCount    = 0;
      let   noCoordCount = 0;

      rows.forEach(row => {
        const lat = row.lat ? parseFloat(row.lat) : null;
        const lng = row.lng ? parseFloat(row.lng) : null;

        if (!lat || !lng) {
          noCoordCount++;
          uniqueRows.push(row); // no coords — cannot check, allow upload
          return;
        }

        // Within-file duplicate
        if (seenInFile.some(s => isSameLocation(lat, lng, s.lat, s.lng))) {
          dupCount++;
          return;
        }
        // Already in Firestore
        if (existingCoords.some(e => isSameLocation(lat, lng, e.lat, e.lng))) {
          dupCount++;
          return;
        }

        seenInFile.push({ lat, lng });
        uniqueRows.push(row);
      });

      setUploadPreview({ unique: uniqueRows.length, duplicates: dupCount, noCoords: noCoordCount, pendingRows: uniqueRows });
    } catch (err) {
      console.error("[UPLOAD ERROR]:", err.message, err.code);
      flash(`Upload failed: ${err.message}${err.code ? ` (${err.code})` : ""}`, true);
    }
    setUploading(false);
  }

  // ── Confirm upload — write only unique rows to Firestore in batches of 500 ──
  async function handleConfirmUpload() {
    if (!uploadPreview?.pendingRows?.length) return;
    setUploading(true);
    try {
      const toImport = uploadPreview.pendingRows;

      // Fetch sections fresh for GPS zone detection
      const sectionsSnap = await getDocs(collection(db, "companies", company.id, "sections"));
      const sectionsList = sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log(`[UPLOAD] Sections available: ${sectionsList.length}`);

      function detectZone(lat, lng) {
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) return "";
        for (const sec of sectionsList) {
          if (!sec.polygon || sec.polygon.length < 3) continue;
          // Support both {lat,lng} and {latitude,longitude} formats; ensure numbers
          const poly = sec.polygon
            .map(p => ({
              latitude:  parseFloat(p.lat  ?? p.latitude),
              longitude: parseFloat(p.lng  ?? p.longitude),
            }))
            .filter(p => !isNaN(p.latitude) && !isNaN(p.longitude));
          if (poly.length < 3) continue;
          if (isPointInPolygon({ latitude: lat, longitude: lng }, poly)) return sec.id;
        }
        return "";
      }

      function resolveSection(nameOrId) {
        if (!nameOrId) return "";
        if (sectionsList.find(s => s.id === nameOrId)) return nameOrId;
        const byName = sectionsList.find(s => s.name?.toLowerCase().trim() === nameOrId.toLowerCase().trim());
        return byName ? byName.id : "";
      }

      const BATCH_SIZE = 500;
      let written = 0;

      for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
        const chunk = toImport.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);

        // eslint-disable-next-line no-loop-func
        chunk.forEach(row => {
          const lat = parseFloat(row.lat) || null;
          const lng = parseFloat(row.lng) || null;
          // GPS zone detection first, then name resolution fallback
          const autoSection = (lat && lng) ? detectZone(lat, lng) : "";
          const finalSection = autoSection || resolveSection(row.section || "");

          const clientId  = "client-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 6);
          const clientRef = doc(db, "companies", company.id, "clients", clientId);
          const fullName  = `${row.first_name || ""} ${row.last_name || ""}`.trim();
          batch.set(clientRef, {
            ...row,
            name:     fullName,
            section:  finalSection,
            location: (lat && lng) ? { lat, lng } : null,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          });
          written++;
        });

        await batch.commit();
        console.log(`[UPLOAD] Batch ${Math.floor(i / BATCH_SIZE) + 1} committed — ${written} clients written`);
      }

      setUploadPreview(null);
      flash(`✅ Imported ${written} clients successfully!`);
      onReload();
    } catch (err) {
      console.error("[UPLOAD ERROR]:", err.message, err.code);
      flash(`Upload failed: ${err.message}${err.code ? ` (${err.code})` : ""}`, true);
    }
    setUploading(false);
  }

  function normalizeRow(row) {
    // Map common column names to standard fields
    const get = (...keys) => {
      for (const k of keys) {
        const found = Object.keys(row).find(rk => rk.toLowerCase().replace(/\s/g, "_") === k.toLowerCase());
        if (found && row[found] !== "") return String(row[found]).trim();
      }
      return "";
    };
    return {
      first_name: get("first_name", "firstname", "name", "nom", "الاسم"),
      last_name:  get("last_name", "lastname", "surname"),
      phone:      get("phone", "telephone", "mobile", "tel", "الهاتف"),
      address:    get("address", "adresse", "العنوان"),
      lat:        parseFloat(get("lat", "latitude")) || null,
      lng:        parseFloat(get("lng", "longitude", "lon")) || null,
      notes:      get("notes", "note", "ملاحظات"),
      section:    get("section", "zone", "area", "المنطقة"),
    };
  }

  async function parseKML(file) {
    const text   = await file.text();
    const parser = new DOMParser();
    const xml    = parser.parseFromString(text, "text/xml");
    const placemarks = xml.querySelectorAll("Placemark");
    const rows = [];
    placemarks.forEach(p => {
      const name   = p.querySelector("name")?.textContent?.trim() || "";
      const coords = p.querySelector("Point coordinates")?.textContent?.trim() || "";
      const desc   = p.querySelector("description")?.textContent?.trim() || "";
      if (coords) {
        const [lng, lat] = coords.split(",").map(Number);
        rows.push({ first_name: name, lat, lng, notes: desc });
      }
    });
    return rows;
  }

  // Bulk-assign zones to all clients that have GPS but no section.
  // Fetches sections + all clients fresh, then processes in chunks to avoid freezing.
  async function handleBulkAssignZones() {
    if (assigningZones) return;
    setAssigningZones(true);
    setAssignResult(null);
    setAssignProgress(0);
    try {
      // 1. Fetch sections
      const sectionsSnap = await getDocs(collection(db, "companies", company.id, "sections"));
      const zonedSections = sectionsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.polygon && s.polygon.length >= 3)
        .map(s => ({
          id:      s.id,
          polygon: s.polygon
            .map(p => ({
              latitude:  parseFloat(p.lat  ?? p.latitude),
              longitude: parseFloat(p.lng  ?? p.longitude),
            }))
            .filter(p => !isNaN(p.latitude) && !isNaN(p.longitude)),
        }))
        .filter(s => s.polygon.length >= 3);

      if (zonedSections.length === 0) {
        setAssignResult({ assigned: 0, skipped: 0 });
        setAssigningZones(false);
        return;
      }

      // 2. Fetch all clients without a section (paginated)
      const unzoned = [];
      let last = null;
      while (true) {
        let q = query(collection(db, "companies", company.id, "clients"), limit(500));
        if (last) q = query(collection(db, "companies", company.id, "clients"), startAfter(last), limit(500));
        const snap = await getDocs(q);
        snap.docs.forEach(d => {
          const data = d.data();
          if (!data.section) unzoned.push({ id: d.id, ...data });
        });
        if (snap.docs.length < 500) break;
        last = snap.docs[snap.docs.length - 1];
      }

      // 3. Compute zone for each client in chunks of 100, yielding between chunks
      const toUpdate = [];
      const CHUNK    = 100;
      for (let i = 0; i < unzoned.length; i += CHUNK) {
        await new Promise(resolve => setTimeout(resolve, 0));
        setAssignProgress(Math.round((i / Math.max(unzoned.length, 1)) * 90));
        const slice = unzoned.slice(i, i + CHUNK);
        for (const client of slice) {
          const lat = parseFloat(client.location?.lat ?? client.lat);
          const lng = parseFloat(client.location?.lng ?? client.lng);
          if (isNaN(lat) || isNaN(lng)) continue;
          for (const s of zonedSections) {
            if (isPointInPolygon({ latitude: lat, longitude: lng }, s.polygon)) {
              toUpdate.push({ id: client.id, sectionId: s.id });
              break;
            }
          }
        }
      }

      // 4. Write to Firestore in batches of 500
      const BATCH_SIZE = 500;
      for (let i = 0; i < toUpdate.length; i += BATCH_SIZE) {
        const chunk = toUpdate.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(({ id, sectionId }) => {
          batch.update(doc(db, "companies", company.id, "clients", id), { section: sectionId });
        });
        await batch.commit();
      }

      setAssignResult({ assigned: toUpdate.length, skipped: unzoned.length - toUpdate.length });
      if (toUpdate.length > 0) onReload();
    } catch (err) {
      flash(`Zone assign error: ${err.message}`, true);
    }
    setAssigningZones(false);
    setAssignProgress(0);
  }

  async function downloadClients() {
    setDownloading(true);
    try {
      const BATCH = 500;
      const allClients = [];
      let last = null;
      while (true) {
        let q = query(
          collection(db, "companies", company.id, "clients"),
          orderBy("created_at", "desc"),
          limit(BATCH)
        );
        if (last) {
          q = query(
            collection(db, "companies", company.id, "clients"),
            orderBy("created_at", "desc"),
            startAfter(last),
            limit(BATCH)
          );
        }
        const snap = await getDocs(q);
        allClients.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })));
        if (snap.docs.length < BATCH) break;
        last = snap.docs[snap.docs.length - 1];
      }
      const rows = allClients.map(c => ({
        "First Name": c.first_name || "",
        "Last Name":  c.last_name  || "",
        "Phone":      c.phone      || "",
        "Address":    c.address    || "",
        "Lat":        c.location?.lat || c.lat || "",
        "Lng":        c.location?.lng || c.lng || "",
        "Section":    c.section    || "",
        "Notes":      c.notes      || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Clients");
      XLSX.writeFile(wb, `${company.name || company.id}_clients.xlsx`);
      flash(`✅ Downloaded ${allClients.length} clients`);
    } catch (err) {
      flash(`Download failed: ${err.message}${err.code ? ` (${err.code})` : ""}`, true);
    }
    setDownloading(false);
  }

  return (
    <div className="space-y-4">

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-gray-800">{company.client_count || clientCount}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("sa_clients_count") || "Total Clients"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-green-600">—</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_with_gps") || "With GPS"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-blue-600">—</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_assigned_zone") || "Assigned to Zone"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-purple-600">—</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_visited_stat") || "Visited"}</p>
        </div>
      </div>

      {/* Info message */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-blue-700 text-sm flex items-center gap-2">
          <Info className="w-4 h-4 flex-shrink-0" />
          {t("lbl_client_import_info") || "Client details are managed from the company's Admin Dashboard. Super Admin can see counts and statistics here only."}
        </p>
      </div>

      {/* Import / Export */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Upload className="w-4 h-4" /> {t("lbl_import_export_clients") || "Import / Export Clients"}</h3>

        {uploadError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-3">{uploadError}</div>}
        {uploadMsg   && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm mb-3">{uploadMsg}</div>}

        <div className="flex flex-wrap gap-3">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.kml" className="hidden" onChange={handleFileUpload} />
          <button onClick={() => fileInputRef.current.click()} disabled={uploading}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {uploading ? <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> {t("btn_import") || "Importing..."}</span> : <span className="flex items-center gap-1.5"><Upload className="w-4 h-4" /> {t("btn_import_clients") || "Import Clients"}</span>}
          </button>
          <button onClick={downloadClients} disabled={downloading || clientCount === 0}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {downloading ? <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> {t("loading") || "Downloading..."}</span> : <span className="flex items-center gap-1.5"><Upload className="w-3.5 h-3.5 rotate-180" /> {t("btn_download_all") || "Download All"} ({company.client_count || clientCount})</span>}
          </button>
          <button onClick={() => setDeleteAllModal(true)} disabled={clientCount === 0}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            <Trash2 className="w-4 h-4" /> {t("btn_delete_all_clients") || "Delete All Clients"}
          </button>
          <button onClick={handleBulkAssignZones} disabled={assigningZones || clientCount === 0}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {assigningZones
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Assigning zones... {assignProgress}%</>
              : <>🎯 Assign All to Zones</>}
          </button>
        </div>
        {assignResult && (
          <div className={`mt-3 text-sm rounded-lg px-4 py-2 ${assignResult.assigned > 0 ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
            {assignResult.assigned > 0
              ? `✅ ${assignResult.assigned} clients assigned to zones, ${assignResult.skipped} outside all zone polygons`
              : `⚠️ No clients matched any zone polygon — check that zones have drawn polygons`}
          </div>
        )}
        {/* Upload preview — shown after file is parsed, before confirming write to Firestore */}
        {uploadPreview && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{uploadPreview.unique}</p>
                <p className="text-xs text-green-700 mt-0.5">{t("lbl_will_be_uploaded") || "Will be uploaded"}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{uploadPreview.duplicates}</p>
                <p className="text-xs text-red-700 mt-0.5">{t("lbl_duplicates_skipped") || "Duplicates skipped"}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-gray-600">{uploadPreview.noCoords}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t("lbl_no_coordinates") || "No coordinates"}</p>
              </div>
            </div>
            {uploadPreview.unique > 0 ? (
              <button onClick={handleConfirmUpload} disabled={uploading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium py-2.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2">
                {uploading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("loading") || "Uploading..."}</>
                  : `${t("btn_import") || "Upload"} ${uploadPreview.unique} client${uploadPreview.unique !== 1 ? "s" : ""}`}
              </button>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-yellow-700 text-sm font-medium">{t("lbl_all_duplicates_msg") || "All clients in this file already exist — nothing to upload"}</p>
              </div>
            )}
            <button onClick={() => setUploadPreview(null)} disabled={uploading}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium py-2 rounded-xl disabled:opacity-50">
              {t("cancel") || "Cancel"}
            </button>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-2">
          Supported formats: Excel (.xlsx, .xls), CSV (.csv), KML (.kml) —
          Columns: first_name, last_name, phone, address, lat, lng, section, notes
        </p>
      </div>

      {/* ── Delete All Confirmation Modal ── */}
      {deleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-800">{t("lbl_delete_all_clients_title") || "Delete All Clients"}</h3>
                <p className="text-xs text-gray-500">{company.name || company.id}</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 space-y-1">
              <p className="font-semibold">{t("sa_delete_company") || "This will permanently delete all"} {company.client_count || clientCount} {t("sa_clients_count") || "clients"}.</p>
              <p>{t("lbl_confirm_reset_title") || "This action cannot be undone. All client data, GPS locations, and visit history will be lost."}</p>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setDeleteAllModal(false)} disabled={deletingAll}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 disabled:opacity-50">
                {t("cancel") || "Cancel"}
              </button>
              <button onClick={handleDeleteAll} disabled={deletingAll}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                {deletingAll
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("loading") || "Deleting..."}</>
                  : <><Trash2 className="w-4 h-4" /> {t("btn_yes_delete_all") || "Yes, Delete All"}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Zones Tab ──────────────────────────────────────────────────────────────
// ── Reset Check-ins Tab ─────────────────────────────────────────────────────
function ResetCheckinsTab({ company, sections }) {
  const { t } = useLanguage();
  const [selectedZone, setSelectedZone] = useState("all"); // "all" or sectionId
  const [checkedInClients, setCheckedInClients] = useState([]); // clients with last_status=arrived
  const [loadingClients, setLoadingClients]     = useState(true);
  const [confirm, setConfirm]   = useState(false);
  const [resetting, setResetting] = useState(false);
  const [result, setResult]     = useState(null); // { clientsReset, cooldownsCleared }
  const [error, setError]       = useState("");

  // Load all checked-in clients once on mount
  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(
          collection(db, "companies", company.id, "clients"),
          where("last_status", "==", "arrived")
        ));
        setCheckedInClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError("Failed to load clients: " + err.message);
      }
      setLoadingClients(false);
    }
    load();
  }, [company.id]);

  // Clients in the current selection
  const filtered = selectedZone === "all"
    ? checkedInClients
    : checkedInClients.filter(c => c.section === selectedZone);

  const selectedZoneName = selectedZone === "all"
    ? (t("lbl_all_zones_company") || "All Zones")
    : sections.find(s => s.id === selectedZone)?.name || selectedZone;

  async function handleReset() {
    setResetting(true);
    setError("");
    setResult(null);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";
      const clientIds = selectedZone === "all" ? null : filtered.map(c => c.id);
      const res = await fetch(`${BACKEND}/api/admin/reset-checkins`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-internal-secret": "pinpoint-internal-2026",
        },
        body: JSON.stringify({ companyId: company.id, clientIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reset failed");
      setResult(data);
      // Refresh the checked-in list
      setCheckedInClients(prev =>
        selectedZone === "all"
          ? []
          : prev.filter(c => c.section !== selectedZone)
      );
    } catch (err) {
      setError(err.message);
    }
    setResetting(false);
    setConfirm(false);
  }

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800">{t("tab_reset_checkins") || "Reset Check-ins"}</h3>
            <p className="text-xs text-gray-400">{t("lbl_tab_reset_desc") || "Clears visited status so vendors can check in again today"}</p>
          </div>
        </div>
      </div>

      {/* Zone selector */}
      <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t("lbl_select_scope") || "Select Scope"}</label>
          <select
            value={selectedZone}
            onChange={e => { setSelectedZone(e.target.value); setResult(null); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            <option value="all">{t("lbl_all_zones_company") || "All Zones (entire company)"}</option>
            {sections.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Count badge */}
        {loadingClients ? (
          <p className="text-sm text-gray-400">{t("lbl_loading_checked_in") || "Loading checked-in clients..."}</p>
        ) : (
          <div className={`rounded-xl p-4 flex items-center justify-between ${filtered.length > 0 ? "bg-orange-50 border border-orange-200" : "bg-gray-50 border border-gray-200"}`}>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {filtered.length} client{filtered.length !== 1 ? "s" : ""} {t("lbl_currently_checked_in") || "currently checked in"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">in {selectedZoneName}</p>
            </div>
            {filtered.length > 0 && (
              <span className="text-2xl font-extrabold text-orange-500">{filtered.length}</span>
            )}
          </div>
        )}

        {/* Checked-in client list preview */}
        {!loadingClients && filtered.length > 0 && (
          <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
            {filtered.map(c => (
              <div key={c.id} className="px-3 py-2 flex items-center justify-between text-sm">
                <span className="font-medium text-gray-800">
                  {c.first_name || c.name || "?"} {c.last_name || ""}
                </span>
                <span className="text-xs text-green-600 font-medium">● {t("lbl_visited_badge") || "Visited"}</span>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
            ✅ Reset complete — <strong>{result.clientsReset}</strong> client{result.clientsReset !== 1 ? "s" : ""} cleared,
            <strong> {result.cooldownsCleared}</strong> cooldown{result.cooldownsCleared !== 1 ? "s" : ""} removed from memory.
            Vendors can now check in again.
          </div>
        )}

        {/* Reset button */}
        <button
          onClick={() => setConfirm(true)}
          disabled={loadingClients || filtered.length === 0 || resetting}
          className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-sm transition"
        >
          <RotateCcw className="w-4 h-4" />
          Reset {filtered.length} Client{filtered.length !== 1 ? "s" : ""} in {selectedZoneName}
        </button>
      </div>

      {/* Confirmation modal */}
      {confirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                  <RotateCcw className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="font-bold text-gray-800">{t("lbl_confirm_reset_title") || "Confirm Reset"}</h3>
              </div>
              <p className="text-sm text-gray-600">
                {t("lbl_confirm_reset_title") || "This will clear the visited status for"} <strong>{filtered.length} client{filtered.length !== 1 ? "s" : ""}</strong> in <strong>{selectedZoneName}</strong>.
                {t("lbl_reset_complete_msg") || "Vendors will be able to check in to them again today. This cannot be undone."}
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirm(false)} disabled={resetting}
                  className="flex-1 border border-gray-200 text-gray-600 font-medium py-2.5 rounded-xl text-sm">
                  {t("cancel") || "Cancel"}
                </button>
                <button onClick={handleReset} disabled={resetting}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                  {resetting ? (t("lbl_resetting") || "Resetting...") : (t("btn_confirm_reset") || "Confirm Reset")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ZonesTab({ company, sections, onReload }) {
  const { t } = useLanguage();
  const companyId = company.id;
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");

  const ZONE_COLORS_SA = [
    "#1A56A0","#2563EB","#38A169","#16A34A","#E53E3E","#DC2626",
    "#805AD5","#7C3AED","#DD6B20","#EA580C","#319795","#475569",
  ];

  function flash(msg, isErr = false) {
    if (isErr) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 3000);
  }

  function escapeXml(str) {
    return String(str || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  }

  function handleDownloadGeoJSON() {
    const geojson = {
      type: "FeatureCollection",
      features: sections.filter(s => s.polygon?.length > 2).map(s => ({
        type: "Feature",
        properties: { id: s.id, name: s.name, color: s.color, description: s.description || "" },
        geometry: {
          type: "Polygon",
          coordinates: [[...s.polygon.map(p => [p.lng, p.lat]), [s.polygon[0].lng, s.polygon[0].lat]]],
        },
      })),
    };
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${company.name || "zones"}.geojson`; a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadKML() {
    const placemarks = sections.filter(s => s.polygon?.length > 2).map(s => {
      const coords = [...s.polygon, s.polygon[0]].map(p => `${p.lng},${p.lat},0`).join(" ");
      return `  <Placemark>\n    <name>${escapeXml(s.name)}</name>\n    <description>${escapeXml(s.description || "")}</description>\n    <Polygon><outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs></Polygon>\n  </Placemark>`;
    }).join("\n");
    const kml = `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n  <name>${escapeXml(company.name || "Zones")}</name>\n${placemarks}\n</Document>\n</kml>`;
    const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${company.name || "zones"}.kml`; a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true); setError("");
    try {
      const text = await file.text();
      let zones = [];
      if (file.name.endsWith(".geojson") || file.name.endsWith(".json")) {
        const gj = JSON.parse(text);
        const features = gj.type === "FeatureCollection" ? gj.features : [gj];
        for (const f of features) {
          if (f.geometry?.type !== "Polygon") continue;
          const polygon = f.geometry.coordinates[0].slice(0,-1).map(c => ({ lat: c[1], lng: c[0] }));
          if (polygon.length < 3) continue;
          zones.push({ name: f.properties?.name || "Imported Zone", color: f.properties?.color || ZONE_COLORS_SA[0], description: f.properties?.description || "", polygon });
        }
      } else if (file.name.endsWith(".kml")) {
        const xmlDoc = new DOMParser().parseFromString(text, "text/xml");
        for (const pm of xmlDoc.getElementsByTagName("Placemark")) {
          const name = pm.getElementsByTagName("name")[0]?.textContent || "Imported Zone";
          const desc = pm.getElementsByTagName("description")[0]?.textContent || "";
          const coordsEl = pm.getElementsByTagName("coordinates")[0];
          if (!coordsEl) continue;
          const polygon = coordsEl.textContent.trim().split(/\s+/).map(c => { const [lng,lat] = c.split(",").map(Number); return {lat,lng}; }).filter(p => !isNaN(p.lat));
          if (polygon.length < 3) continue;
          const last = polygon[polygon.length-1];
          if (last.lat === polygon[0].lat && last.lng === polygon[0].lng) polygon.pop();
          zones.push({ name, color: ZONE_COLORS_SA[0], description: desc, polygon });
        }
      } else {
        throw new Error("Unsupported file type. Use .geojson or .kml.");
      }
      if (zones.length === 0) throw new Error("No valid polygon zones found in file.");
      for (const z of zones) {
        const sectionId = "section-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2,6);
        await setDoc(doc(db, "companies", companyId, "sections", sectionId), { ...z, created_at: serverTimestamp(), updated_at: serverTimestamp() });
        try { await updateDoc(doc(db, "companies", companyId), { section_count: increment(1) }); } catch(_) {}
      }
      flash(`✅ Imported ${zones.length} zone(s)!`);
      onReload?.();
    } catch (err) {
      flash("Import error: " + err.message, true);
    }
    setImporting(false);
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-600 font-medium">{sections.length} zone(s)</p>
        <div className="flex items-center gap-2">
          <input ref={importFileRef} type="file" accept=".geojson,.json,.kml" className="hidden" onChange={handleImportFile} />
          <button onClick={() => importFileRef.current?.click()} disabled={importing}
            className="flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-50">
            <Upload className="w-4 h-4" />{importing ? (t("loading") || "Importing...") : (t("btn_import") || "Import")}
          </button>
          {sections.length > 0 && (
            <div className="relative group">
              <button className="flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-1.5 rounded-lg transition">
                <Download className="w-4 h-4" /> {t("btn_export") || "Export"}
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 hidden group-hover:block">
                <button onClick={handleDownloadGeoJSON} className="w-full text-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">GeoJSON</button>
                <button onClick={handleDownloadKML} className="w-full text-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">KML</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}
      <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
        {sections.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">{t("lbl_no_zones_yet") || "No zones defined yet. Import a GeoJSON or KML file to add zones."}</div>
        ) : sections.map(s => (
          <div key={s.id} className="p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: s.color || "#6366f1" }}><MapIcon className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-medium text-gray-800">{s.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.polygon ? `${s.polygon.length} ${t("lbl_polygon_points") || "polygon points"}` : (t("lbl_no_polygon_defined") || "No polygon defined")}</p>
              {s.description && <p className="text-xs text-gray-300 mt-0.5">{s.description}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Company Analytics Tab ───────────────────────────────────────────────────
function CompanyAnalytics({ company }) {
  const { t } = useLanguage();
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [period,     setPeriod]     = useState("7days");
  const [analytics,  setAnalytics]  = useState(null);
  const [chartData,  setChartData]  = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [events,     setEvents]     = useState([]);
  const [saTimezone, setSaTimezone] = useState("Asia/Dubai");

  useEffect(() => {
    async function loadTimezone() {
      try {
        const snap = await getDoc(doc(db, "platform", "settings"));
        if (snap.exists() && snap.data().superadmin_timezone) {
          setSaTimezone(snap.data().superadmin_timezone);
        }
      } catch (err) {
        console.error("[Analytics] Timezone load:", err.message);
      }
    }
    loadTimezone();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!company.id) return;
    loadAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id, period]);

  async function loadAnalytics() {
    setLoading(true);
    setError("");
    try {
      const days      = period === "7days" ? 7 : period === "30days" ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);

      const eventsSnap = await getDocs(
        query(
          collection(db, "companies", company.id, "events"),
          where("timestamp", ">=", startDate),
          limit(1000)
        )
      );
      const allEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEvents(allEvents);

      const checkins    = allEvents.filter(e => e.type === "checkin");
      const alerts      = allEvents.filter(e => e.type?.includes("alert") || e.type === "zone_alert");
      const speedAlerts = allEvents.filter(e => e.type === "speed_alert");
      const longStops   = allEvents.filter(e => e.type === "long_stop_alert");

      setAnalytics({
        totalEvents:       allEvents.length,
        totalCheckins:     checkins.length,
        totalAlerts:       alerts.length,
        speedAlerts:       speedAlerts.length,
        longStopAlerts:    longStops.length,
        avgCheckinsPerDay: checkins.length > 0 ? (checkins.length / days).toFixed(1) : 0,
        uniqueVendors:     new Set(checkins.map(e => e.vendor_id).filter(Boolean)).size,
        uniqueClients:     new Set(checkins.map(e => e.client_id).filter(Boolean)).size,
      });

      // Daily chart data
      const byDate = {};
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const label = d.toLocaleDateString("en-GB", { timeZone: saTimezone, day: "2-digit", month: "short" });
        byDate[label] = { date: label, visits: 0, alerts: 0 };
      }
      allEvents.forEach(e => {
        if (!e.timestamp) return;
        const d     = e.timestamp.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
        const label = d.toLocaleDateString("en-GB", { timeZone: saTimezone, day: "2-digit", month: "short" });
        if (!byDate[label]) return;
        if (e.type === "checkin") byDate[label].visits++;
        if (e.type?.includes("alert") || e.type === "zone_alert") byDate[label].alerts++;
      });
      setChartData(Object.values(byDate));

      // Top vendors
      const vendorCounts = {};
      checkins.forEach(e => {
        if (!e.vendor_id) return;
        vendorCounts[e.vendor_id] = vendorCounts[e.vendor_id] || { name: e.vendor_name || e.vendor_id, count: 0 };
        vendorCounts[e.vendor_id].count++;
      });
      setTopVendors(Object.values(vendorCounts).sort((a, b) => b.count - a.count).slice(0, 5));

      // Top clients
      const clientCounts = {};
      checkins.forEach(e => {
        if (!e.client_id) return;
        clientCounts[e.client_id] = clientCounts[e.client_id] || { name: e.client_name || e.client_id, count: 0 };
        clientCounts[e.client_id].count++;
      });
      setTopClients(Object.values(clientCounts).sort((a, b) => b.count - a.count).slice(0, 5));

    } catch (err) {
      setError(`Failed to load analytics: ${err.message}${err.code ? ` (${err.code})` : ""}`);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("lbl_loading_analytics") || "Loading analytics..."}</p>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
      <p className="text-red-700 text-sm font-medium">{error}</p>
      <button onClick={loadAnalytics} className="mt-3 text-sm text-red-600 underline">{t("sa_retry") || "Retry"}</button>
    </div>
  );

  const dayCount = period === "7days" ? 7 : period === "30days" ? 30 : 90;

  return (
    <div className="space-y-4">

      {/* Header + Period Selector */}
      <div className="bg-white rounded-xl shadow-sm p-5 flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <BarChart2 className="w-4 h-4" /> {t("lbl_usage_analytics") || "Usage Analytics"}
        </h3>
        <div className="flex gap-1">
          {[{ id: "7days", label: t("lbl_7_days") || "7 Days" }, { id: "30days", label: t("lbl_30_days") || "30 Days" }, { id: "90days", label: t("lbl_90_days") || "90 Days" }].map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                period === p.id ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-green-600">{analytics?.totalCheckins || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_total_checkins") || "Total Check-ins"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-blue-600">{analytics?.avgCheckinsPerDay || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_avg_visits_day") || "Avg Visits / Day"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-purple-600">{analytics?.uniqueVendors || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_active_vendors_stat") || "Active Vendors"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-red-600">{analytics?.totalAlerts || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_total_alerts_stat") || "Total Alerts"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-orange-600">{analytics?.speedAlerts || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_speed_alerts_stat") || "Speed Alerts"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-yellow-600">{analytics?.longStopAlerts || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_long_stop_alerts_stat") || "Long Stop Alerts"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-gray-700">{analytics?.uniqueClients || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_clients_visited") || "Clients Visited"}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-gray-700">{analytics?.totalEvents || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">{t("lbl_total_events") || "Total Events"}</p>
        </div>
      </div>

      {/* Daily Activity Chart */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">
          {t("lbl_daily_activity") || "Daily Activity"} — {dayCount} {t("lbl_days") || "Days"}
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }}
              interval={period === "7days" ? 0 : period === "30days" ? 4 : 13} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="visits" fill="#10B981" radius={[3, 3, 0, 0]} name="Visits" />
            <Bar dataKey="alerts" fill="#EF4444" radius={[3, 3, 0, 0]} name="Alerts" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Vendors + Top Clients */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Truck className="w-4 h-4" /> {t("lbl_top_vendors_visits") || "Top Vendors by Visits"}
          </h3>
          {topVendors.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">{t("lbl_no_visits_recorded") || "No visits recorded"}</p>
          ) : (
            <div className="space-y-3">
              {topVendors.map((vendor, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate">{vendor.name}</span>
                      <span className="text-sm font-bold text-green-600 ml-2 flex-shrink-0">{vendor.count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full"
                        style={{ width: `${(vendor.count / topVendors[0].count) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> {t("lbl_most_visited_clients") || "Most Visited Clients"}
          </h3>
          {topClients.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">{t("lbl_no_visits_recorded") || "No visits recorded"}</p>
          ) : (
            <div className="space-y-3">
              {topClients.map((client, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate">{client.name}</span>
                      <span className="text-sm font-bold text-blue-600 ml-2 flex-shrink-0">{client.count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full"
                        style={{ width: `${(client.count / topClients[0].count) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alert Breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {t("lbl_alert_breakdown") || "Alert Breakdown"}
        </h3>
        {(analytics?.totalAlerts || 0) === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">{t("lbl_no_alerts_period") || "No alerts in this period — great performance!"}</p>
        ) : (
          <div className="space-y-3">
            {[
              { label: t("lbl_speed_alerts_stat") || "Speed Alerts",          value: analytics?.speedAlerts    || 0,                                               color: "bg-red-500"    },
              { label: t("lbl_long_stop_alerts_stat") || "Long Stop Alerts",   value: analytics?.longStopAlerts || 0,                                               color: "bg-orange-500" },
              { label: t("lbl_zone_alerts") || "Zone Alerts",                  value: events.filter(e => e.type === "zone_alert").length,                           color: "bg-blue-500"   },
              { label: t("lbl_outside_hours_alerts") || "Outside Hours Alerts",value: events.filter(e => e.type === "outside_hours_alert").length,                  color: "bg-purple-500" },
            ].filter(item => item.value > 0).map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className="text-sm font-bold text-gray-800">{item.value}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`${item.color} h-2 rounded-full`}
                      style={{ width: `${analytics.totalAlerts > 0 ? (item.value / analytics.totalAlerts) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

// ── Create Company ─────────────────────────────────────────────────────────
function CreateCompany({ onDone }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "", plan: "starter",
    billing_plan: "expiry_date",
    billing_deposit: "", billing_monthly_fee: "", billing_balance: "",
    subscription_expiry: "",
    admin_email: "", admin_password: "",
    admin_first_name: "", admin_last_name: "",
    out_of_zone_alerts: true, max_vendors: 10, max_clients: 1000,
  });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState("");
  const [showPass, setShowPass]   = useState(false);

  // Load global settings as defaults when component mounts
  React.useEffect(() => {
    async function loadGlobalDefaults() {
      try {
        const snap = await getDoc(doc(db, "platform", "settings"));
        if (snap.exists()) {
          const s = snap.data();
          setForm(p => ({
            ...p,
            out_of_zone_alerts: s.out_of_zone_alerts !== false,
            max_vendors:        s.default_max_vendors  || p.max_vendors,
            max_clients:        s.default_max_clients  || p.max_clients,
          }));
        }
      } catch (err) { console.error("Could not load global defaults:", err); }
    }
    loadGlobalDefaults();
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim())              return setError("Company name is required.");
    if (!form.admin_email.trim())       return setError("Admin email is required.");
    if (!form.admin_password.trim())    return setError("Admin password is required.");
    if (!form.admin_first_name.trim())  return setError("Admin first name is required.");
    if (form.admin_password.length < 6) return setError("Password must be at least 6 characters.");
    setSaving(true);
    try {
      const companyId = "company-" + Date.now().toString(36);
      const billingData = {};
      if (form.billing_plan === "monthly") {
        billingData.billing_deposit     = parseFloat(form.billing_deposit)     || 0;
        billingData.billing_monthly_fee = parseFloat(form.billing_monthly_fee) || 0;
        billingData.billing_balance     = parseFloat(form.billing_balance)     || 0;
        billingData.subscription_status = billingData.billing_balance > 0 ? "paid" : "unpaid";
      } else if (form.billing_plan === "forever") {
        billingData.subscription_status = "paid";
      } else if (form.billing_plan === "expiry_date") {
        if (form.subscription_expiry) {
          billingData.subscription_expiry = new Date(form.subscription_expiry);
          billingData.subscription_status = new Date(form.subscription_expiry) > new Date() ? "paid" : "unpaid";
        } else {
          billingData.subscription_status = "trial";
        }
        billingData.block_on_expiry = true;
      }
      await setDoc(doc(db, "companies", companyId), {
        name: form.name.trim(), plan: form.plan,
        billing_plan: form.billing_plan,
        active: true,
        out_of_zone_alerts: form.out_of_zone_alerts,
        max_vendors: parseInt(form.max_vendors), max_clients: parseInt(form.max_clients),
        ...billingData,
        created_at: serverTimestamp(), updated_at: serverTimestamp(),
      });
      await setDoc(doc(db, "companies", companyId, "profile", "info"), {
        name: form.name.trim(), primary_color: "#16a34a", language: "en", created_at: serverTimestamp(),
      });
      const adminId = "admin-" + Date.now().toString(36);
      await setDoc(doc(db, "companies", companyId, "users", adminId), {
        first_name: form.admin_first_name.trim(), last_name: form.admin_last_name.trim(),
        name: `${form.admin_first_name.trim()} ${form.admin_last_name.trim()}`.trim(),
        email: form.admin_email.trim(), password: form.admin_password.trim(),
        role: "admin", active: true,
        created_at: serverTimestamp(), updated_at: serverTimestamp(),
      });
      setSuccess(`✅ Company "${form.name}" created! Admin account is being set up automatically.`);
      setForm({ name: "", plan: "starter", billing_plan: "expiry_date", billing_deposit: "", billing_monthly_fee: "", billing_balance: "", subscription_expiry: "", admin_email: "", admin_password: "", admin_first_name: "", admin_last_name: "", out_of_zone_alerts: true, max_vendors: 10, max_clients: 1000 });
      setTimeout(() => { setSuccess(""); onDone(); }, 3000);
    } catch (err) { setError("Error: " + err.message); }
    setSaving(false);
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{t("create_company_title") || "Create New Company"}</h2>
      <p className="text-gray-500 text-sm mb-6">{t("create_company_desc") || "Creates the company + admin account automatically."}</p>
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm mb-4">{success}</div>}
      <form onSubmit={handleCreate} className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b flex items-center gap-2"><Building2 className="w-4 h-4" /> {t("lbl_company_info") || "Company Info"}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_company_name") || "Company Name"} *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="e.g. Al Hanini Trading LLC" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_tier") || "Tier"}</label>
              <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_out_of_zone_alerts") || "Out-of-Zone Alerts"}</label>
              <select value={form.out_of_zone_alerts}
                onChange={e => setForm(p => ({ ...p, out_of_zone_alerts: e.target.value === "true" }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="true">{t("enabled") || "Enabled"}</option>
                <option value="false">{t("disabled") || "Disabled"}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_max_vendors") || "Max Vendors"}</label>
              <input type="number" min={1} value={form.max_vendors} onChange={e => setForm(p => ({ ...p, max_vendors: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_max_clients") || "Max Clients"}</label>
              <input type="number" min={1} value={form.max_clients} onChange={e => setForm(p => ({ ...p, max_clients: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b flex items-center gap-2"><UserCheck className="w-4 h-4" /> {t("lbl_admin_account_section") || "Company Admin Account"}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_first_name") || "First Name"} *</label>
              <input value={form.admin_first_name} onChange={e => setForm(p => ({ ...p, admin_first_name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" placeholder={t("lbl_first_name") || "First name"} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_last_name") || "Last Name"}</label>
              <input value={form.admin_last_name} onChange={e => setForm(p => ({ ...p, admin_last_name: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" placeholder={t("lbl_last_name") || "Last name"} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_admin_email") || "Admin Email"} *</label>
              <input type="email" value={form.admin_email} onChange={e => setForm(p => ({ ...p, admin_email: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" placeholder="admin@company.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_admin_password") || "Admin Password"} *</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={form.admin_password}
                  onChange={e => setForm(p => ({ ...p, admin_password: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder={t("lbl_min_6_chars") || "Min 6 characters"} />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  className="absolute right-2 top-2 text-xs text-gray-400 hover:text-gray-600 px-1 py-0.5">
                  {showPass ? (t("form_hide") || "Hide") : (t("form_show") || "Show")}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-blue-700 text-sm flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> {t("lbl_admin_auto_created") || "Admin login account created automatically."}</p>
        </div>
        <button type="submit" disabled={saving}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium px-6 py-2.5 rounded-lg disabled:opacity-50">
          {saving ? (t("lbl_creating") || "Creating...") : <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {t("btn_create_company") || "Create Company"}</span>}
        </button>
      </form>
    </div>
  );
}

// ── Global Settings ────────────────────────────────────────────────────────
function GlobalSettings() {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({
    working_hours_start: "08:00", working_hours_end: "23:00",
    nearby_distance: 30, alert_cooldown: 15, auto_checkin: true,
    max_speed_alert: 0, default_language: "en",
    default_timezone: "Asia/Dubai", default_date_format: "DD/MM/YYYY",
    min_nearby_distance: 10, min_alert_cooldown: 5,
    offline_threshold_minutes: 60,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError]     = useState("");

  useEffect(() => { loadSettings(); }, []);

  async function loadSettings() {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "platform", "settings"));
      if (snap.exists()) setSettings(prev => ({ ...prev, ...snap.data() }));
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "platform", "settings"), { ...settings, updated_at: serverTimestamp() });
      // Note: these settings will be auto-applied the next time a new company is created
      setSuccess("✅ Global settings saved!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  if (loading) return <div className="bg-white rounded-xl shadow-sm p-12 text-center"><p className="text-gray-400 text-sm">{t("sa_loading") || "Loading..."}</p></div>;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{t("sa_global_settings") || "Global Default Settings"}</h2>
      <p className="text-gray-500 text-sm mb-6">{t("sa_global_settings_sub") || "Defaults applied to all new companies."}</p>
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm mb-4">{success}</div>}
      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b flex items-center gap-2"><Clock className="w-4 h-4" /> {t("lbl_working_hours") || "Working Hours"}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_wh_start") || "Start"}</label>
              <input type="time" value={settings.working_hours_start}
                onChange={e => setSettings(p => ({ ...p, working_hours_start: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_wh_end") || "End"}</label>
              <input type="time" value={settings.working_hours_end}
                onChange={e => setSettings(p => ({ ...p, working_hours_end: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b flex items-center gap-2"><PlusCircle className="w-4 h-4" /> {t("lbl_new_company_defaults") || "New Company Defaults"}</h3>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_default_max_vendors") || "Default Max Vendors"}</label>
              <input type="number" min={1} value={settings.default_max_vendors || 10}
                onChange={e => setSettings(p => ({ ...p, default_max_vendors: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_default_max_clients") || "Default Max Clients"}</label>
              <input type="number" min={1} value={settings.default_max_clients || 1000}
                onChange={e => setSettings(p => ({ ...p, default_max_clients: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_default_ooz_alerts") || "Default Out-of-Zone Alerts"}</label>
              <select value={settings.out_of_zone_alerts !== false ? "true" : "false"}
                onChange={e => setSettings(p => ({ ...p, out_of_zone_alerts: e.target.value === "true" }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="true">{t("enabled") || "Enabled"}</option>
                <option value="false">{t("disabled") || "Disabled"}</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-blue-600 bg-blue-50 rounded-lg p-3 mb-4 flex items-center gap-2">
            <Lightbulb className="w-3.5 h-3.5 flex-shrink-0" /> These values are automatically pre-filled when creating a new company.
          </p>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b flex items-center gap-2"><MapPin className="w-4 h-4" /> {t("lbl_tracking") || "Tracking"}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_nearby_distance_m") || "Nearby Distance (m)"}</label>
              <input type="number" min={10} value={settings.nearby_distance}
                onChange={e => setSettings(p => ({ ...p, nearby_distance: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_min_distance_m") || "Min Allowed Distance (m)"}</label>
              <input type="number" min={5} value={settings.min_nearby_distance}
                onChange={e => setSettings(p => ({ ...p, min_nearby_distance: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_alert_cooldown_min") || "Alert Cooldown (min)"}</label>
              <input type="number" min={1} value={settings.alert_cooldown}
                onChange={e => setSettings(p => ({ ...p, alert_cooldown: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_max_speed_kmh") || "Max Speed Alert (km/h, 0=off)"}</label>
              <input type="number" min={0} value={settings.max_speed_alert}
                onChange={e => setSettings(p => ({ ...p, max_speed_alert: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_offline_threshold_min") || "Offline threshold (minutes)"}</label>
              <input type="number" min={10} max={1440} value={settings.offline_threshold_minutes ?? 60}
                onChange={e => setSettings(p => ({ ...p, offline_threshold_minutes: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              <p className="text-xs text-gray-400 mt-1">{t("lbl_offline_threshold_hint") || "A vendor is marked offline if no GPS data is received for this many minutes"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <label className="text-sm font-medium text-gray-700">{t("lbl_auto_checkin") || "Auto Check-in:"}</label>
            <button type="button" onClick={() => setSettings(p => ({ ...p, auto_checkin: !p.auto_checkin }))}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${settings.auto_checkin ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {settings.auto_checkin
                ? <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> {t("enabled") || "Enabled"}</span>
                : <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block" /> {t("disabled") || "Disabled"}</span>
              }
            </button>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b flex items-center gap-2"><MapPin className="w-4 h-4" /> {t("lbl_regional") || "Regional"}</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_language") || "Language"}</label>
              <select value={settings.default_language} onChange={e => setSettings(p => ({ ...p, default_language: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_timezone") || "Timezone"}</label>
              <select value={settings.default_timezone} onChange={e => setSettings(p => ({ ...p, default_timezone: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <optgroup label="Middle East">
                  <option value="Asia/Dubai">Dubai / UAE (UTC+4)</option>
                  <option value="Asia/Riyadh">Saudi Arabia (UTC+3)</option>
                  <option value="Asia/Kuwait">Kuwait (UTC+3)</option>
                  <option value="Asia/Qatar">Qatar (UTC+3)</option>
                  <option value="Asia/Bahrain">Bahrain (UTC+3)</option>
                  <option value="Asia/Muscat">Oman (UTC+4)</option>
                  <option value="Asia/Baghdad">Iraq (UTC+3)</option>
                  <option value="Asia/Beirut">Lebanon (UTC+2/+3)</option>
                  <option value="Asia/Amman">Jordan (UTC+2/+3)</option>
                  <option value="Asia/Damascus">Syria (UTC+2/+3)</option>
                  <option value="Asia/Jerusalem">Israel / Palestine (UTC+2/+3)</option>
                  <option value="Asia/Aden">Yemen (UTC+3)</option>
                </optgroup>
                <optgroup label="North Africa">
                  <option value="Africa/Casablanca">Morocco (UTC+1)</option>
                  <option value="Africa/Algiers">Algeria (UTC+1)</option>
                  <option value="Africa/Tunis">Tunisia (UTC+1)</option>
                  <option value="Africa/Tripoli">Libya (UTC+2)</option>
                  <option value="Africa/Cairo">Egypt (UTC+2)</option>
                  <option value="Africa/Khartoum">Sudan (UTC+3)</option>
                </optgroup>
                <optgroup label="West Africa">
                  <option value="Africa/Dakar">Senegal (UTC+0)</option>
                  <option value="Africa/Abidjan">Ivory Coast (UTC+0)</option>
                  <option value="Africa/Accra">Ghana (UTC+0)</option>
                  <option value="Africa/Lagos">Nigeria (UTC+1)</option>
                  <option value="Africa/Bamako">Mali (UTC+0)</option>
                  <option value="Africa/Conakry">Guinea (UTC+0)</option>
                  <option value="Africa/Douala">Cameroon (UTC+1)</option>
                </optgroup>
                <optgroup label="East Africa">
                  <option value="Africa/Nairobi">Kenya / East Africa (UTC+3)</option>
                  <option value="Africa/Addis_Ababa">Ethiopia (UTC+3)</option>
                  <option value="Africa/Dar_es_Salaam">Tanzania (UTC+3)</option>
                  <option value="Africa/Kampala">Uganda (UTC+3)</option>
                </optgroup>
                <optgroup label="Southern Africa">
                  <option value="Africa/Johannesburg">South Africa (UTC+2)</option>
                  <option value="Africa/Lusaka">Zambia (UTC+2)</option>
                  <option value="Africa/Harare">Zimbabwe (UTC+2)</option>
                </optgroup>
                <optgroup label="Europe">
                  <option value="Europe/London">UK / Ireland (UTC+0/+1)</option>
                  <option value="Europe/Paris">France / Central Europe (UTC+1/+2)</option>
                  <option value="Europe/Berlin">Germany (UTC+1/+2)</option>
                  <option value="Europe/Madrid">Spain (UTC+1/+2)</option>
                  <option value="Europe/Rome">Italy (UTC+1/+2)</option>
                  <option value="Europe/Amsterdam">Netherlands (UTC+1/+2)</option>
                  <option value="Europe/Brussels">Belgium (UTC+1/+2)</option>
                  <option value="Europe/Zurich">Switzerland (UTC+1/+2)</option>
                  <option value="Europe/Stockholm">Sweden (UTC+1/+2)</option>
                  <option value="Europe/Istanbul">Turkey (UTC+3)</option>
                  <option value="Europe/Moscow">Russia / Moscow (UTC+3)</option>
                </optgroup>
                <optgroup label="Asia">
                  <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                  <option value="Asia/Kolkata">India (UTC+5:30)</option>
                  <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                  <option value="Asia/Colombo">Sri Lanka (UTC+5:30)</option>
                  <option value="Asia/Bangkok">Thailand (UTC+7)</option>
                  <option value="Asia/Singapore">Singapore / Malaysia (UTC+8)</option>
                  <option value="Asia/Shanghai">China (UTC+8)</option>
                  <option value="Asia/Tokyo">Japan (UTC+9)</option>
                  <option value="Asia/Seoul">South Korea (UTC+9)</option>
                </optgroup>
                <optgroup label="Americas">
                  <option value="America/New_York">US Eastern (UTC-5/-4)</option>
                  <option value="America/Chicago">US Central (UTC-6/-5)</option>
                  <option value="America/Denver">US Mountain (UTC-7/-6)</option>
                  <option value="America/Los_Angeles">US Pacific (UTC-8/-7)</option>
                  <option value="America/Sao_Paulo">Brazil (UTC-3)</option>
                  <option value="America/Mexico_City">Mexico (UTC-6/-5)</option>
                </optgroup>
                <optgroup label="Universal">
                  <option value="UTC">UTC (UTC+0)</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("lbl_date_format") || "Date Format"}</label>
              <select value={settings.default_date_format} onChange={e => setSettings(p => ({ ...p, default_date_format: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </div>
        <button type="submit" disabled={saving}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium px-6 py-2.5 rounded-lg disabled:opacity-50">
          {saving ? (t("saving") || "Saving...") : <span className="flex items-center gap-1.5"><Save className="w-4 h-4" /> {t("btn_save_global_settings") || "Save Global Settings"}</span>}
        </button>
      </form>
    </div>
  );
}

// ── Platform Health ────────────────────────────────────────────────────────
function PlatformHealth({ onOpenCompany }) {
  const { t } = useLanguage();
  const [companies,    setCompanies]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const [error,        setError]        = useState("");
  const [chartData,    setChartData]    = useState([]);
  const [saTimezone,   setSaTimezone]   = useState("Asia/Dubai");

  useEffect(() => {
    async function loadTimezone() {
      try {
        const snap = await getDoc(doc(db, "platform", "settings"));
        if (snap.exists() && snap.data().superadmin_timezone) {
          setSaTimezone(snap.data().superadmin_timezone);
        }
      } catch (err) {
        console.error("[PH] Timezone load:", err.message);
      }
    }
    loadTimezone();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadHealth() {
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(collection(db, "companies"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.last_activity?.toDate ? a.last_activity.toDate() : new Date(0);
        const tb = b.last_activity?.toDate ? b.last_activity.toDate() : new Date(0);
        return tb - ta;
      });
      setCompanies(list);
      setLastRefresh(new Date());
      await loadChartData();
    } catch (err) {
      setError(`Failed to load: ${err.message}${err.code ? ` (${err.code})` : ""}`);
    } finally {
      setLoading(false);
    }
  }

  async function loadChartData() {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      sevenDaysAgo.setHours(0, 0, 0, 0);

      const snap = await getDocs(
        query(
          collectionGroup(db, "events"),
          where("type", "==", "checkin"),
          where("timestamp", ">=", sevenDaysAgo),
          limit(500)
        )
      );

      const byDate = {};
      snap.docs.forEach(d => {
        const event = d.data();
        const date  = event.timestamp?.toDate
          ? event.timestamp.toDate().toLocaleDateString("en-GB", { timeZone: saTimezone, day: "2-digit", month: "short" })
          : null;
        if (date) byDate[date] = (byDate[date] || 0) + 1;
      });

      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const label = d.toLocaleDateString("en-GB", { timeZone: saTimezone, day: "2-digit", month: "short" });
        days.push({ date: label, visits: byDate[label] || 0 });
      }
      setChartData(days);
    } catch (err) {
      console.error("[CHART] load error:", err.message);
    }
  }

  function timeAgo(timestamp) {
    if (!timestamp) return t("lbl_never") || "Never";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diff  = Date.now() - date.getTime();
    const mins  = Math.floor(diff / 60000);
    const hrs   = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)  return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24)  return `${hrs}h ago`;
    return `${days}d ago`;
  }

  function formatTimestampInTZ(timestamp) {
    if (!timestamp) return t("lbl_never") || "Never";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("en-GB", {
      timeZone: saTimezone,
      day:      "2-digit",
      month:    "short",
      hour:     "2-digit",
      minute:   "2-digit",
      hour12:   true,
    });
  }

  function isGpsActive(company) {
    if (!company.last_activity) return false;
    const last = company.last_activity.toDate
      ? company.last_activity.toDate()
      : new Date(company.last_activity);
    return (Date.now() - last.getTime()) < 30 * 60 * 1000;
  }

  function getCompanyStatus(company) {
    if (company.active === false) return { label: "Suspended", color: "bg-red-100 text-red-700" };
    if (company.billing_plan === "expiry_date" && company.subscription_expiry) {
      const expiry = company.subscription_expiry.toDate
        ? company.subscription_expiry.toDate()
        : new Date(company.subscription_expiry);
      const daysLeft = (expiry - new Date()) / (1000 * 60 * 60 * 24);
      if (daysLeft < 0)  return { label: "Expired",                   color: "bg-red-100 text-red-700"    };
      if (daysLeft <= 7) return { label: `${Math.floor(daysLeft)}d left`, color: "bg-yellow-100 text-yellow-700" };
    }
    if (company.billing_plan === "monthly" && (company.billing_balance || 0) <= 0) {
      return { label: "Balance 0", color: "bg-red-100 text-red-700" };
    }
    return { label: "Active", color: "bg-green-100 text-green-700" };
  }

  const totalCompanies     = companies.length;
  const activeCompanies    = companies.filter(c => c.active !== false).length;
  const suspendedCompanies = companies.filter(c => c.active === false).length;
  const totalVendors       = companies.reduce((s, c) => s + (c.vendor_count  || 0), 0);
  const totalClients       = companies.reduce((s, c) => s + (c.client_count  || 0), 0);
  const totalVisitsToday   = companies.reduce((s, c) => s + (c.visits_today  || 0), 0);
  const totalAlertsToday   = companies.reduce((s, c) => s + (c.alerts_today  || 0), 0);
  const expiringSoon       = companies.filter(c => {
    if (c.billing_plan !== "expiry_date" || !c.subscription_expiry) return false;
    const expiry = c.subscription_expiry.toDate
      ? c.subscription_expiry.toDate()
      : new Date(c.subscription_expiry);
    const daysLeft = (expiry - new Date()) / (1000 * 60 * 60 * 24);
    return daysLeft > 0 && daysLeft <= 7;
  }).length;

  const inactiveCompanies = companies.filter(company => {
    if (!company.last_activity) return true;
    const last = company.last_activity.toDate
      ? company.last_activity.toDate()
      : new Date(company.last_activity);
    return (Date.now() - last.getTime()) > 7 * 24 * 60 * 60 * 1000;
  });

  if (loading && companies.length === 0) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("sa_loading_health") || "Loading platform health data..."}</p>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
      <p className="text-red-700 text-sm font-medium">{error}</p>
      <button onClick={loadHealth} className="mt-3 text-sm text-red-600 underline">{t("sa_retry") || "Retry"}</button>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-5 flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Activity className="w-5 h-5 text-yellow-500" /> {t("nav_health") || "Platform Health"}
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">{t("sa_platform_health_sub") || "Live overview of all companies and activity"}</p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-400">{t("sa_updated") || "Updated"} {timeAgo(lastRefresh)}</span>
          )}
          <button
            onClick={loadHealth}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1.5 rounded-lg disabled:opacity-50 transition">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {t("btn_refresh") || "Refresh"}
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Building2}     value={totalCompanies}     label={t("lbl_total_companies") || "Total Companies"}  color="blue"   />
        <StatCard icon={CheckCircle}   value={activeCompanies}    label={t("active")              || "Active"}           color="green"  />
        <StatCard icon={Lock}          value={suspendedCompanies} label={t("lbl_suspended")       || "Suspended"}        color="red"    />
        <StatCard icon={Truck}         value={totalVendors}       label={t("lbl_total_vendors")   || "Total Vendors"}    color="purple" />
        <StatCard icon={Users}         value={totalClients}       label={t("lbl_total_clients")   || "Total Clients"}    color="blue"   />
        <StatCard icon={CheckCircle}   value={totalVisitsToday}   label={t("lbl_visits_today")    || "Visits Today"}     color="green"  />
        <StatCard icon={AlertTriangle} value={totalAlertsToday}   label={t("overview_alerts_today") || "Alerts Today"}  color="yellow" />
        <StatCard icon={Calendar}      value={expiringSoon}       label={t("lbl_expiring_soon")   || "Expiring in 7 days"} color="red" />
      </div>

      {/* 7-Day Visits Chart */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <BarChart2 className="w-4 h-4" /> {t("sa_platform_visits") || "Platform Visits — Last 7 Days"}
        </h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="visits" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Visits" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Inactive Companies Warning */}
      {inactiveCompanies.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {inactiveCompanies.length} {t("sa_company_gps_warning") || "Companies — No GPS Activity for 7+ Days"}
          </h3>
          <p className="text-xs text-orange-600 mb-3">
            {t("sa_gps_warning_sub") || "These companies may need follow-up. Their vendors may have stopped using the platform."}
          </p>
          <div className="space-y-2">
            {inactiveCompanies.map(company => (
              <div key={company.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{company.name || company.id}</p>
                  <p className="text-xs text-gray-400">{t("sa_last_activity") || "Last activity:"} {formatTimestampInTZ(company.last_activity)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getCompanyStatus(company).color}`}>
                    {getCompanyStatus(company).label}
                  </span>
                  <button
                    onClick={() => onOpenCompany(company)}
                    className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded-lg transition">
                    {t("sa_view") || "View"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Status Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> {t("nav_all_companies") || "All Companies"} ({totalCompanies})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("lbl_company_name") || "Company"}</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("lbl_billing_plan") || "Plan"}</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("tab_clients") || "Clients"}</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("tab_vendors") || "Vendors"}</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("lbl_visits_today") || "Visits Today"}</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("lbl_last_activity") || "Last Activity"}</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">GPS</th>
                <th className="text-start py-3 px-4 text-xs font-semibold text-gray-500">{t("lbl_status") || "Status"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companies.map(company => {
                const status      = getCompanyStatus(company);
                const gpsActive   = isGpsActive(company);
                const isSuspended = company.active === false;
                const isWarning   = status.label.includes("left") || status.label === "Expired" || status.label === "Balance 0";
                return (
                  <tr
                    key={company.id}
                    onClick={() => onOpenCompany(company)}
                    className={`hover:bg-gray-50 transition cursor-pointer ${
                      isSuspended ? "bg-red-50" : isWarning ? "bg-yellow-50" : ""
                    }`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {company.logo_url
                          ? <img src={company.logo_url} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                          : <div className="w-7 h-7 rounded-lg bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {(company.name || "?").charAt(0).toUpperCase()}
                            </div>
                        }
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{company.name || company.id}</p>
                          <p className="text-xs text-gray-400 font-mono truncate">{company.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
                        {company.billing_plan || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700">{(company.client_count || 0).toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-700">{company.vendor_count || 0}</td>
                    <td className="py-3 px-4">
                      <span className={`text-sm font-medium ${(company.visits_today || 0) > 0 ? "text-green-600" : "text-gray-400"}`}>
                        {company.visits_today || 0}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{formatTimestampInTZ(company.last_activity)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${gpsActive ? "bg-green-500" : "bg-gray-300"}`} />
                        <span className={`text-xs ${gpsActive ? "text-green-600" : "text-gray-400"}`}>
                          {gpsActive ? (t("lbl_gps_live") || "Live") : (t("offline") || "Offline")}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

// ── Session History Panel (shared by SuperAdminSettings and CompanySessionsTab) ──
function SessionHistoryPanel({ collectionPath, showRole }) {
  const { t } = useLanguage();
  const defaultFrom = new Date();
  defaultFrom.setDate(defaultFrom.getDate() - 7);
  const toISO = d => d.toISOString().slice(0, 10);

  const [fromDate,  setFromDate]  = useState(toISO(defaultFrom));
  const [toDate,    setToDate]    = useState(toISO(new Date()));
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [loaded,    setLoaded]    = useState(false);
  const [error,     setError]     = useState("");

  async function loadHistory() {
    setLoading(true);
    setError("");
    try {
      const from = new Date(fromDate + "T00:00:00");
      const to   = new Date(toDate   + "T23:59:59");
      const ref  = collection(db, ...collectionPath);
      const snap = await getDocs(
        query(ref,
          where("login_at", ">=", from),
          where("login_at", "<=", to),
          orderBy("login_at", "desc"),
          limit(100)
        )
      );
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoaded(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function statusBadge(session) {
    if (session.revoked)       return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{t("lbl_session_revoked") || "Revoked"}</span>;
    if (session.active)        return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t("lbl_active") || "Active"}</span>;
    return                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t("lbl_session_ended") || "Ended"}</span>;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
        <Clock className="w-4 h-4" />
        {t("lbl_connection_history") || "Connection History"}
      </h3>

      {/* Date range filter */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t("lbl_from_date") || "From"}</label>
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t("lbl_to_date") || "To"}</label>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <button
          onClick={loadHistory}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
          {loading
            ? <><Loader2 className="w-3 h-3 animate-spin" /> {t("loading") || "Loading..."}</>
            : t("btn_load_history") || "Load"
          }
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
          <p className="text-red-600 text-xs font-mono">{error}</p>
        </div>
      )}

      {!loaded ? (
        <p className="text-gray-400 text-sm text-center py-6">{t("lbl_select_dates") || "Select a date range and click Load."}</p>
      ) : history.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-6">{t("lbl_no_history") || "No sessions found for this period."}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="border-b text-gray-500 font-medium">
                <th className="pb-2 pr-4">{t("lbl_logged_in") || "Login Time"}</th>
                <th className="pb-2 pr-4">{t("lbl_ip_address") || "IP Address"}</th>
                <th className="pb-2 pr-4">{t("lbl_device") || "Device"}</th>
                {showRole && <th className="pb-2 pr-4">{t("lbl_role") || "Role"}</th>}
                <th className="pb-2">{t("lbl_status") || "Status"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map(session => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                    {session.login_at?.toDate ? session.login_at.toDate().toLocaleString() : "—"}
                  </td>
                  <td className="py-2 pr-4 font-mono text-gray-600">{session.ip_address || "—"}</td>
                  <td className="py-2 pr-4 text-gray-600">
                    {session.device?.browser || "?"} / {session.device?.os || "?"}
                  </td>
                  {showRole && (
                    <td className="py-2 pr-4">
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full capitalize">{session.role || "—"}</span>
                    </td>
                  )}
                  <td className="py-2">{statusBadge(session)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Company Sessions Tab ────────────────────────────────────────────────────
function CompanySessionsTab({ company }) {
  const { t } = useLanguage();
  const [sessions,  setSessions]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [revoking,  setRevoking]  = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(); }, [company.id]);

  async function loadSessions() {
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(
        query(
          collection(db, "companies", company.id, "sessions"),
          orderBy("login_at", "desc"),
          limit(50)
        )
      );
      const active = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.active === true)
        .slice(0, 20);
      setSessions(active);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke(sessionId) {
    setRevoking(sessionId);
    try {
      const fns    = getFunctions(undefined, "europe-west1");
      const revoke = httpsCallable(fns, "revokeSession");
      await revoke({ sessionId, companyId: company.id });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      console.error("[SESSION] Revoke error:", err.message);
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="space-y-4">
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4 pb-2 border-b">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Monitor className="w-4 h-4" />
          {t("lbl_active_sessions") || "Active Sessions"}
        </h3>
        <button
          onClick={loadSessions}
          disabled={loading}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          {t("btn_refresh") || "Refresh"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
          <p className="text-red-600 text-xs font-mono">{error}</p>
        </div>
      )}

      {loading ? (
        <p className="text-gray-400 text-sm text-center py-8">{t("loading") || "Loading..."}</p>
      ) : sessions.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-8">{t("lbl_no_sessions") || "No active sessions found"}</p>
      ) : (
        <div className="space-y-3">
          {sessions.map(session => {
            const loginTime  = session.login_at?.toDate  ? session.login_at.toDate().toLocaleString()  : "—";
            const lastActive = session.last_active?.toDate ? session.last_active.toDate().toLocaleString() : "—";
            return (
              <div key={session.id} className="flex items-start justify-between p-3 rounded-xl border border-gray-200 bg-gray-50">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <Monitor className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {session.device?.browser || "Unknown Browser"}
                      {" on "}
                      {session.device?.os || "Unknown OS"}
                      {session.role && (
                        <span className="ms-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">
                          {session.role}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t("lbl_logged_in") || "Logged in"}: {loginTime}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t("lbl_last_active") || "Last active"}: {lastActive}
                    </p>
                    <p className="text-xs text-gray-400 font-mono">
                      {t("lbl_ip_address") || "IP"}: {session.ip_address || "—"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(session.id)}
                  disabled={revoking === session.id}
                  className="text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition disabled:opacity-50 flex-shrink-0">
                  {revoking === session.id
                    ? (t("lbl_revoking") || "Revoking...")
                    : (t("btn_revoke") || "Revoke")
                  }
                </button>
              </div>
            );
          })}
        </div>
      )}

      {sessions.length > 1 && (
        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
          <p className="text-yellow-700 text-xs">
            {t("lbl_multiple_sessions_warning") || "Multiple active sessions detected. Revoke any sessions you do not recognize."}
          </p>
        </div>
      )}
    </div>
    <SessionHistoryPanel collectionPath={["companies", company.id, "sessions"]} showRole={true} />
    </div>
  );
}

// ── SuperAdmin My Settings ──────────────────────────────────────────────────
function SuperAdminSettings({ isDarkMode, toggleDarkMode }) {
  const { lang, setLang, t } = useLanguage();

  const [currentPassword,      setCurrentPassword]      = useState("");
  const [newPassword,          setNewPassword]          = useState("");
  const [confirmPassword,      setConfirmPassword]      = useState("");
  const [changingPassword,     setChangingPassword]     = useState(false);
  const [passwordError,        setPasswordError]        = useState("");
  const [passwordSuccess,      setPasswordSuccess]      = useState("");

  const [newEmail,             setNewEmail]             = useState("");
  const [emailConfirmPassword, setEmailConfirmPassword] = useState("");
  const [changingEmail,        setChangingEmail]        = useState(false);
  const [emailError,           setEmailError]           = useState("");
  const [emailSuccess,         setEmailSuccess]         = useState("");
  const [currentUserEmail,     setCurrentUserEmail]     = useState("");

  useEffect(() => {
    import("firebase/auth").then(({ getAuth }) => {
      const user = getAuth().currentUser;
      if (user) setCurrentUserEmail(user.email || "");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 2FA state ──
  const [twoFAEnabled,  setTwoFAEnabled]  = useState(false);
  const [twoFAStep,     setTwoFAStep]     = useState("idle"); // idle | setup | verify | disable
  const [qrCodeUrl,     setQrCodeUrl]     = useState("");
  const [twoFACode,     setTwoFACode]     = useState("");
  const [twoFAError,    setTwoFAError]    = useState("");
  const [twoFALoading,  setTwoFALoading]  = useState(false);

  useEffect(() => {
    async function load2FAStatus() {
      try {
        const snap = await getDoc(doc(db, "platform", "super_admin_2fa"));
        if (snap.exists()) setTwoFAEnabled(snap.data().enabled === true);
      } catch (err) {
        console.error("[2FA] status load error:", err.message);
      }
    }
    load2FAStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Active Sessions state ──
  const [sessions,          setSessions]          = useState([]);
  const [loadingSessions,   setLoadingSessions]   = useState(false);
  const [sessionsError,     setSessionsError]     = useState("");
  const [revokingSession,   setRevokingSession]   = useState(null);
  const currentSessionId = localStorage.getItem("pinpoint_session_id");

  useEffect(() => {
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Timezone state ──
  const [saTimezone,     setSaTimezone]     = useState("Asia/Dubai");
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [timezoneMsg,    setTimezoneMsg]    = useState("");

  useEffect(() => {
    async function loadSATimezone() {
      try {
        const snap = await getDoc(doc(db, "platform", "settings"));
        if (snap.exists() && snap.data().superadmin_timezone) {
          setSaTimezone(snap.data().superadmin_timezone);
        }
      } catch (err) {
        console.error("[SA TIMEZONE] Load error:", err.message);
      }
    }
    loadSATimezone();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSaveTimezone() {
    setSavingTimezone(true);
    setTimezoneMsg("");
    try {
      await setDoc(
        doc(db, "platform", "settings"),
        { superadmin_timezone: saTimezone },
        { merge: true }
      );
      setTimezoneMsg(t("success_saved") || "Saved successfully.");
      setTimeout(() => setTimezoneMsg(""), 3000);
    } catch (err) {
      setTimezoneMsg(`Error: ${err.message} (${err.code})`);
    } finally {
      setSavingTimezone(false);
    }
  }

  // ── Branding state ──
  const { branding: currentBranding } = useBranding();
  const [platformName,   setPlatformName]   = useState(currentBranding.platform_name || "Pinpoint");
  const [primaryColor,   setPrimaryColor]   = useState(currentBranding.primary_color || "#F59E0B");
  const [logoFile,       setLogoFile]       = useState(null);
  const [logoPreview,    setLogoPreview]    = useState(currentBranding.logo_url || null);
  const [logoRemoved,    setLogoRemoved]    = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingMsg,    setBrandingMsg]    = useState("");

  useEffect(() => {
    setPlatformName(currentBranding.platform_name || "Pinpoint");
    setPrimaryColor(currentBranding.primary_color || "#F59E0B");
    setLogoPreview(currentBranding.logo_url || null);
    setLogoRemoved(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranding.platform_name, currentBranding.primary_color, currentBranding.logo_url]);

  async function handleSaveBranding() {
    setSavingBranding(true);
    setBrandingMsg("");
    try {
      // Start with the existing logo URL — only change it if user uploaded or explicitly removed
      let logoUrl = currentBranding.logo_url || null;

      if (logoFile) {
        const logoRef = storageRef(storage, "platform/logo");
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      } else if (logoRemoved) {
        logoUrl = null;
      }

      await setDoc(
        doc(db, "platform", "branding"),
        {
          platform_name: platformName.trim() || "Pinpoint",
          primary_color: primaryColor,
          logo_url:      logoUrl,
          updated_at:    serverTimestamp(),
        },
        { merge: true }
      );

      setBrandingMsg(t("success_branding_saved") || "Branding saved. Changes apply immediately across all pages.");
      setTimeout(() => setBrandingMsg(""), 5000);
    } catch (err) {
      setBrandingMsg(`Error: ${err.message} (${err.code})`);
    } finally {
      setSavingBranding(false);
    }
  }

  function handleLogoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setBrandingMsg(t("err_logo_type") || "Please select an image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setBrandingMsg(t("err_logo_size") || "Logo must be under 2MB.");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  // ── Backup state ──
  const [backupSettings, setBackupSettings] = useState({
    enabled:        false,
    frequency:      "daily",
    custom_days:    10,
    time_hour:      2,
    retain_backups: 30,
  });
  const [savingBackup,  setSavingBackup]  = useState(false);
  const [backupMsg,     setBackupMsg]     = useState("");
  const [runningBackup, setRunningBackup] = useState(false);
  const [backupLogs,    setBackupLogs]    = useState([]);

  useEffect(() => {
    async function loadBackupSettings() {
      try {
        const snap = await getDoc(doc(db, "platform", "settings"));
        if (snap.exists() && snap.data().backup) {
          setBackupSettings(prev => ({ ...prev, ...snap.data().backup }));
        }
        const logsSnap = await getDocs(
          query(
            collection(db, "platform", "backup_logs", "logs"),
            orderBy("timestamp", "desc"),
            limit(5)
          )
        );
        setBackupLogs(logsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("[BACKUP] Load settings error:", err.message);
      }
    }
    loadBackupSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSaveBackupSettings() {
    setSavingBackup(true);
    setBackupMsg("");
    try {
      await setDoc(
        doc(db, "platform", "settings"),
        { backup: backupSettings, updated_at: serverTimestamp() },
        { merge: true }
      );
      setBackupMsg(t("success_saved") || "Saved successfully.");
      setTimeout(() => setBackupMsg(""), 3000);
    } catch (err) {
      setBackupMsg(`Error: ${err.message}`);
    } finally {
      setSavingBackup(false);
    }
  }

  async function handleRunBackupNow() {
    setRunningBackup(true);
    setBackupMsg("");
    try {
      const fns           = getFunctions(undefined, "europe-west1");
      const triggerBackup = httpsCallable(fns, "triggerManualBackup");
      await triggerBackup();
      setBackupMsg(t("success_backup_started") || "Backup started. Check logs in a few minutes.");
      // Reload logs after a short delay
      setTimeout(async () => {
        try {
          const logsSnap = await getDocs(
            query(collection(db, "platform", "backup_logs", "logs"), orderBy("timestamp", "desc"), limit(5))
          );
          setBackupLogs(logsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (_) {}
      }, 5000);
    } catch (err) {
      setBackupMsg(`Backup failed: ${err.message}`);
    } finally {
      setRunningBackup(false);
    }
  }

  // ── Data Retention state ──
  const [retentionSettings, setRetentionSettings] = useState({ months: 12 });
  const [savingRetention,   setSavingRetention]   = useState(false);
  const [retentionMsg,      setRetentionMsg]      = useState("");

  useEffect(() => {
    async function loadRetention() {
      try {
        const snap = await getDoc(doc(db, "platform", "settings"));
        if (snap.exists() && snap.data().data_retention) {
          setRetentionSettings(snap.data().data_retention);
        }
      } catch (err) {
        console.error("[RETENTION] Load error:", err.message);
      }
    }
    loadRetention();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSaveRetention() {
    setSavingRetention(true);
    setRetentionMsg("");
    try {
      await setDoc(
        doc(db, "platform", "settings"),
        { data_retention: retentionSettings },
        { merge: true }
      );
      setRetentionMsg(t("success_saved") || "Saved successfully.");
      setTimeout(() => setRetentionMsg(""), 3000);
    } catch (err) {
      setRetentionMsg(`Error: ${err.message}`);
    } finally {
      setSavingRetention(false);
    }
  }

  async function loadSessions() {
    setLoadingSessions(true);
    setSessionsError("");
    try {
      // Order by login_at only (single-field index — no composite index needed).
      // Filter active===true in JS to avoid the composite index requirement.
      const snap = await getDocs(
        query(
          collection(db, "platform", "super_admin_sessions", "sessions"),
          orderBy("login_at", "desc"),
          limit(50)
        )
      );
      const active = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.active === true)
        .slice(0, 20);
      setSessions(active);
    } catch (err) {
      console.error("[SESSION] Load error:", err.message);
      setSessionsError(err.message);
    } finally {
      setLoadingSessions(false);
    }
  }

  async function handleRevokeSession(sessionId) {
    if (sessionId === currentSessionId) return;
    setRevokingSession(sessionId);
    try {
      const fns    = getFunctions(undefined, "europe-west1");
      const revoke = httpsCallable(fns, "revokeSession");
      await revoke({ sessionId });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      console.error("[SESSION] Revoke error:", err.message);
    } finally {
      setRevokingSession(null);
    }
  }

  async function handleSetup2FA() {
    setTwoFALoading(true);
    setTwoFAError("");
    try {
      const fns    = getFunctions(undefined, "europe-west1");
      const result = await httpsCallable(fns, "generate2FASecret")();
      setQrCodeUrl(result.data.qrCodeDataUrl);
      setTwoFAStep("setup");
    } catch (err) {
      setTwoFAError(err.message);
    } finally {
      setTwoFALoading(false);
    }
  }

  async function handleVerify2FA() {
    if (!twoFACode || twoFACode.length !== 6) {
      setTwoFAError(t("err_2fa_code_length") || "Please enter the 6-digit code.");
      return;
    }
    setTwoFALoading(true);
    setTwoFAError("");
    try {
      const fns    = getFunctions(undefined, "europe-west1");
      const result = await httpsCallable(fns, "verify2FA")({ code: twoFACode });
      if (result.data.success) {
        setTwoFAEnabled(true);
        setTwoFAStep("idle");
        setTwoFACode("");
        setQrCodeUrl("");
      } else {
        setTwoFAError(result.data.error || "Invalid code.");
      }
    } catch (err) {
      setTwoFAError(err.message);
    } finally {
      setTwoFALoading(false);
    }
  }

  async function handleDisable2FA() {
    if (!twoFACode || twoFACode.length !== 6) {
      setTwoFAError(t("err_2fa_code_length") || "Please enter the 6-digit code.");
      return;
    }
    setTwoFALoading(true);
    setTwoFAError("");
    try {
      const fns    = getFunctions(undefined, "europe-west1");
      const result = await httpsCallable(fns, "disable2FA")({ code: twoFACode });
      if (result.data.success) {
        setTwoFAEnabled(false);
        setTwoFAStep("idle");
        setTwoFACode("");
      } else {
        setTwoFAError(result.data.error || "Invalid code.");
      }
    } catch (err) {
      setTwoFAError(err.message);
    } finally {
      setTwoFALoading(false);
    }
  }

  async function handleChangePassword() {
    setPasswordError("");
    setPasswordSuccess("");
    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setChangingPassword(true);
    try {
      const { getAuth, reauthenticateWithCredential, EmailAuthProvider, updatePassword } = await import("firebase/auth");
      const auth = getAuth();
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordSuccess("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setPasswordError("Current password is incorrect.");
      } else if (err.code === "auth/weak-password") {
        setPasswordError("New password is too weak.");
      } else {
        setPasswordError(`Error: ${err.message} (${err.code})`);
      }
    } finally {
      setChangingPassword(false);
    }
  }

  async function handleChangeEmail() {
    setEmailError("");
    setEmailSuccess("");
    if (!newEmail.trim()) {
      setEmailError(t("err_email_required") || "Please enter a new email address.");
      return;
    }
    if (!newEmail.includes("@")) {
      setEmailError(t("err_email_invalid") || "Please enter a valid email address.");
      return;
    }
    if (newEmail.trim() === currentUserEmail) {
      setEmailError(t("err_email_same") || "New email is same as current.");
      return;
    }
    if (!emailConfirmPassword) {
      setEmailError(t("err_password_required") || "Please enter your password.");
      return;
    }
    setChangingEmail(true);
    try {
      const { getAuth, reauthenticateWithCredential, EmailAuthProvider, updateEmail } = await import("firebase/auth");
      const auth = getAuth();
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, emailConfirmPassword);
      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail.trim());
      setCurrentUserEmail(newEmail.trim());
      setNewEmail("");
      setEmailConfirmPassword("");
      setEmailSuccess(t("success_email_updated") || "Email updated successfully.");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setEmailError(t("err_wrong_password") || "Password is incorrect.");
      } else if (err.code === "auth/email-already-in-use") {
        setEmailError(t("err_email_in_use") || "Email already in use.");
      } else if (err.code === "auth/invalid-email") {
        setEmailError(t("err_email_invalid") || "Please enter a valid email address.");
      } else if (err.code === "auth/requires-recent-login") {
        setEmailError(t("err_reauth_required") || "Please log out and log back in first.");
      } else {
        setEmailError(`${err.message} (${err.code})`);
      }
    } finally {
      setChangingEmail(false);
    }
  }

  return (
    <div className="space-y-6">

      {/* Page header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800">{t("nav_sa_settings") || "My Settings"}</h2>
        <p className="text-gray-500 text-sm mt-1">{t("sa_settings_sub") || "Language, appearance, and account security"}</p>
      </div>

      {/* Two-column grid on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left column: Language + Appearance */}
        <div className="space-y-6">

          {/* Language */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Globe className="w-4 h-4" /> {t("lbl_language") || "Language"}
            </h3>
            <div className="flex gap-2 flex-wrap">
              {[
                { code: "en", label: "English"  },
                { code: "ar", label: "العربية" },
                { code: "fr", label: "Français" },
              ].map(l => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    lang === l.code
                      ? "bg-yellow-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Appearance */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Moon className="w-4 h-4" /> {t("lbl_appearance") || "Appearance"}
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">{t("lbl_dark_mode") || "Dark Mode"}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t("lbl_dark_mode_hint") || "Switch to dark theme"}</p>
              </div>
              <div dir="ltr">
                <UISwitch checked={isDarkMode} onChange={toggleDarkMode} />
              </div>
            </div>
          </div>

          {/* Two-Factor Authentication */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Shield className="w-4 h-4" /> {t("lbl_two_factor_auth") || "Two-Factor Authentication"}
            </h3>

            {/* Status indicator */}
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-2.5 h-2.5 rounded-full ${twoFAEnabled ? "bg-green-500" : "bg-gray-300"}`} />
              <span className={`text-sm font-medium ${twoFAEnabled ? "text-green-600" : "text-gray-500"}`}>
                {twoFAEnabled ? (t("lbl_2fa_enabled") || "2FA is enabled") : (t("lbl_2fa_disabled") || "2FA is disabled")}
              </span>
            </div>

            {/* Idle — enable or start disable flow */}
            {twoFAStep === "idle" && (
              <button
                onClick={() => twoFAEnabled ? setTwoFAStep("disable") : handleSetup2FA()}
                disabled={twoFALoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 ${
                  twoFAEnabled
                    ? "bg-red-50 hover:bg-red-100 text-red-600"
                    : "bg-yellow-500 hover:bg-yellow-600 text-white"
                }`}>
                <Shield className="w-4 h-4" />
                {twoFALoading
                  ? (t("loading") || "Loading...")
                  : twoFAEnabled
                    ? (t("btn_disable_2fa") || "Disable 2FA")
                    : (t("btn_enable_2fa")  || "Enable 2FA")
                }
              </button>
            )}

            {/* Setup — QR code + verify */}
            {twoFAStep === "setup" && qrCodeUrl && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {t("lbl_2fa_scan_qr") || "Scan this QR code with Google Authenticator or Authy:"}
                </p>
                <div className="flex justify-center">
                  <img src={qrCodeUrl} alt="2FA QR Code" className="w-48 h-48 border border-gray-200 rounded-xl p-2" />
                </div>
                <p className="text-xs text-gray-500 text-center">
                  {t("lbl_2fa_enter_code") || "After scanning, enter the 6-digit code from your app:"}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFACode}
                  onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest text-lg font-mono focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                {twoFAError && <p className="text-red-600 text-xs">{twoFAError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handleVerify2FA}
                    disabled={twoFALoading || twoFACode.length !== 6}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-50">
                    {twoFALoading ? (t("lbl_verifying") || "Verifying...") : (t("btn_verify_enable") || "Verify & Enable")}
                  </button>
                  <button
                    onClick={() => { setTwoFAStep("idle"); setTwoFACode(""); setTwoFAError(""); setQrCodeUrl(""); }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg">
                    {t("cancel") || "Cancel"}
                  </button>
                </div>
              </div>
            )}

            {/* Disable — verify code before disabling */}
            {twoFAStep === "disable" && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  {t("lbl_2fa_enter_to_disable") || "Enter the code to disable 2FA:"}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFACode}
                  onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest text-lg font-mono focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                {twoFAError && <p className="text-red-600 text-xs">{twoFAError}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handleDisable2FA}
                    disabled={twoFALoading || twoFACode.length !== 6}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-50">
                    {twoFALoading ? (t("lbl_disabling") || "Disabling...") : (t("btn_confirm_disable") || "Confirm Disable")}
                  </button>
                  <button
                    onClick={() => { setTwoFAStep("idle"); setTwoFACode(""); setTwoFAError(""); }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg">
                    {t("cancel") || "Cancel"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Active Sessions */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4 pb-2 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                {t("lbl_active_sessions") || "Active Sessions"}
              </h3>
              <button
                onClick={loadSessions}
                disabled={loadingSessions}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50">
                <RefreshCw className={`w-3 h-3 ${loadingSessions ? "animate-spin" : ""}`} />
                {t("btn_refresh") || "Refresh"}
              </button>
            </div>

            {sessionsError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
                <p className="text-red-600 text-xs font-mono">{sessionsError}</p>
              </div>
            )}

            {loadingSessions ? (
              <p className="text-gray-400 text-sm text-center py-4">{t("loading") || "Loading..."}</p>
            ) : sessions.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">{t("lbl_no_sessions") || "No active sessions found"}</p>
            ) : (
              <div className="space-y-3">
                {sessions.map(session => {
                  const isCurrent  = session.id === currentSessionId;
                  const loginTime  = session.login_at?.toDate
                    ? session.login_at.toDate().toLocaleString()
                    : "—";
                  const lastActive = session.last_active?.toDate
                    ? session.last_active.toDate().toLocaleString()
                    : "—";
                  return (
                    <div key={session.id}
                      className={`flex items-start justify-between p-3 rounded-xl border ${
                        isCurrent ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"
                      }`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isCurrent ? "bg-green-100" : "bg-gray-200"
                        }`}>
                          <Monitor className={`w-4 h-4 ${isCurrent ? "text-green-600" : "text-gray-500"}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {session.device?.browser || "Unknown Browser"}
                            {" on "}
                            {session.device?.os || "Unknown OS"}
                            {isCurrent && (
                              <span className="ms-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                                {t("lbl_current_session") || "Current"}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("lbl_logged_in") || "Logged in"}: {loginTime}
                          </p>
                          <p className="text-xs text-gray-400">
                            {t("lbl_last_active") || "Last active"}: {lastActive}
                          </p>
                          <p className="text-xs text-gray-400 font-mono">
                            {t("lbl_ip_address") || "IP"}: {session.ip_address || "—"}
                          </p>
                        </div>
                      </div>
                      {!isCurrent && (
                        <button
                          onClick={() => handleRevokeSession(session.id)}
                          disabled={revokingSession === session.id}
                          className="text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition disabled:opacity-50 flex-shrink-0">
                          {revokingSession === session.id
                            ? (t("lbl_revoking") || "Revoking...")
                            : (t("btn_revoke") || "Revoke")
                          }
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {sessions.length > 1 && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                <p className="text-yellow-700 text-xs">
                  {t("lbl_multiple_sessions_warning") || "Multiple active sessions detected. Revoke any sessions you do not recognize."}
                </p>
              </div>
            )}
          </div>

          {/* Default Timezone */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {t("lbl_default_timezone") || "Default Timezone"}
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              {t("lbl_default_timezone_desc") || "Used for your reports, platform health timestamps, and analytics date grouping."}
            </p>

            <select
              value={saTimezone}
              onChange={e => setSaTimezone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 mb-3">

              <optgroup label="Middle East">
                <option value="Asia/Dubai">Dubai / UAE (UTC+4)</option>
                <option value="Asia/Riyadh">Saudi Arabia (UTC+3)</option>
                <option value="Asia/Kuwait">Kuwait (UTC+3)</option>
                <option value="Asia/Qatar">Qatar (UTC+3)</option>
                <option value="Asia/Bahrain">Bahrain (UTC+3)</option>
                <option value="Asia/Muscat">Oman (UTC+4)</option>
                <option value="Asia/Baghdad">Iraq (UTC+3)</option>
                <option value="Asia/Beirut">Lebanon (UTC+2/+3)</option>
                <option value="Asia/Amman">Jordan (UTC+2/+3)</option>
                <option value="Asia/Damascus">Syria (UTC+2/+3)</option>
                <option value="Asia/Jerusalem">Israel / Palestine (UTC+2/+3)</option>
              </optgroup>

              <optgroup label="North Africa">
                <option value="Africa/Casablanca">Morocco (UTC+1)</option>
                <option value="Africa/Algiers">Algeria (UTC+1)</option>
                <option value="Africa/Tunis">Tunisia (UTC+1)</option>
                <option value="Africa/Tripoli">Libya (UTC+2)</option>
                <option value="Africa/Cairo">Egypt (UTC+2)</option>
                <option value="Africa/Khartoum">Sudan (UTC+3)</option>
              </optgroup>

              <optgroup label="West Africa">
                <option value="Africa/Dakar">Senegal (UTC+0)</option>
                <option value="Africa/Lagos">Nigeria (UTC+1)</option>
                <option value="Africa/Abidjan">Ivory Coast (UTC+0)</option>
                <option value="Africa/Accra">Ghana (UTC+0)</option>
                <option value="Africa/Bamako">Mali (UTC+0)</option>
                <option value="Africa/Douala">Cameroon (UTC+1)</option>
              </optgroup>

              <optgroup label="East Africa">
                <option value="Africa/Nairobi">Kenya (UTC+3)</option>
                <option value="Africa/Addis_Ababa">Ethiopia (UTC+3)</option>
                <option value="Africa/Dar_es_Salaam">Tanzania (UTC+3)</option>
              </optgroup>

              <optgroup label="Southern Africa">
                <option value="Africa/Johannesburg">South Africa (UTC+2)</option>
              </optgroup>

              <optgroup label="Europe">
                <option value="Europe/London">UK (UTC+0/+1)</option>
                <option value="Europe/Paris">France / Central Europe (UTC+1/+2)</option>
                <option value="Europe/Berlin">Germany (UTC+1/+2)</option>
                <option value="Europe/Madrid">Spain (UTC+1/+2)</option>
                <option value="Europe/Istanbul">Turkey (UTC+3)</option>
                <option value="Europe/Moscow">Russia / Moscow (UTC+3)</option>
              </optgroup>

              <optgroup label="Asia">
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Kolkata">India (UTC+5:30)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Asia/Bangkok">Thailand (UTC+7)</option>
                <option value="Asia/Singapore">Singapore / Malaysia (UTC+8)</option>
                <option value="Asia/Shanghai">China (UTC+8)</option>
                <option value="Asia/Tokyo">Japan (UTC+9)</option>
              </optgroup>

              <optgroup label="Americas">
                <option value="America/New_York">US Eastern (UTC-5/-4)</option>
                <option value="America/Chicago">US Central (UTC-6/-5)</option>
                <option value="America/Los_Angeles">US Pacific (UTC-8/-7)</option>
                <option value="America/Sao_Paulo">Brazil (UTC-3)</option>
              </optgroup>

              <optgroup label="Universal">
                <option value="UTC">UTC (UTC+0)</option>
              </optgroup>
            </select>

            {timezoneMsg && (
              <p className={`text-xs mb-2 ${timezoneMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>
                {timezoneMsg}
              </p>
            )}

            <button
              onClick={handleSaveTimezone}
              disabled={savingTimezone}
              className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {savingTimezone
                ? (t("lbl_saving") || "Saving...")
                : <><Save className="w-4 h-4" /> {t("btn_save_timezone") || "Save Timezone"}</>
              }
            </button>
          </div>

        </div>

        {/* Right column: Change Password */}
        <div className="space-y-6">

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Lock className="w-4 h-4" /> {t("lbl_change_password") || "Change Password"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_current_password") || "Current Password"}</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_new_password") || "New Password"}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_confirm_password") || "Confirm New Password"}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              </div>
              {passwordError   && <p className="text-red-600 text-xs">{passwordError}</p>}
              {passwordSuccess && <p className="text-green-600 text-xs">{passwordSuccess}</p>}
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                {changingPassword
                  ? (t("sa_changing_password") || "Changing...")
                  : <><Lock className="w-4 h-4" /> {t("lbl_change_password") || "Change Password"}</>
                }
              </button>
            </div>
          </div>

          {/* Change Email */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Mail className="w-4 h-4" /> {t("lbl_change_email") || "Change Email"}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_current_email") || "Current Email"}</label>
                <input
                  type="email"
                  value={currentUserEmail}
                  disabled
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_new_email") || "New Email"}</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder={t("lbl_new_email") || "Enter new email address"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_confirm_with_password") || "Confirm with Current Password"}</label>
                <input
                  type="password"
                  value={emailConfirmPassword}
                  onChange={e => setEmailConfirmPassword(e.target.value)}
                  placeholder={t("lbl_current_password") || "Enter your password to confirm"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
              </div>
              {emailError   && <p className="text-red-600 text-xs bg-red-50 rounded-lg px-3 py-2">{emailError}</p>}
              {emailSuccess && <p className="text-green-600 text-xs bg-green-50 rounded-lg px-3 py-2">{emailSuccess}</p>}
              <button
                onClick={handleChangeEmail}
                disabled={changingEmail || !newEmail || !emailConfirmPassword}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                {changingEmail
                  ? (t("lbl_updating") || "Updating...")
                  : <><Mail className="w-4 h-4" /> {t("btn_update_email") || "Update Email"}</>
                }
              </button>
            </div>
          </div>

          {/* Platform Branding */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Palette className="w-4 h-4" />
              {t("lbl_platform_branding") || "Platform Branding"}
            </h3>

            <div className="space-y-4">

              {/* Platform Name */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("lbl_platform_name") || "Platform Name"}
                </label>
                <input
                  type="text"
                  value={platformName}
                  onChange={e => setPlatformName(e.target.value)}
                  maxLength={30}
                  placeholder="Pinpoint"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                <p className="text-xs text-gray-400 mt-1">
                  {t("lbl_platform_name_desc") || "Shown on login page, reports, and browser tab."}
                </p>
              </div>

              {/* Primary Color */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("lbl_primary_color") || "Primary Color"}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={e => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer p-1" />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={e => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setPrimaryColor(val);
                    }}
                    maxLength={7}
                    placeholder="#F59E0B"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                  <div className="flex gap-1.5">
                    {["#F59E0B","#3B82F6","#10B981","#EF4444","#8B5CF6","#F97316","#06B6D4"].map(color => (
                      <button
                        key={color}
                        onClick={() => setPrimaryColor(color)}
                        className="w-6 h-6 rounded-full border-2 transition hover:scale-110"
                        style={{ backgroundColor: color, borderColor: primaryColor === color ? "#111827" : "transparent" }}
                        title={color} />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {t("lbl_primary_color_desc") || "Affects buttons, sidebar highlights, and active states."}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <button style={{ backgroundColor: primaryColor }} className="px-3 py-1.5 rounded-lg text-white text-xs font-medium">
                    {t("lbl_preview_button") || "Preview Button"}
                  </button>
                  <div style={{ border: `1px solid ${primaryColor}` }} className="px-3 py-1.5 rounded-lg text-xs font-medium">
                    <span style={{ color: primaryColor }}>{t("lbl_preview_badge") || "Preview Badge"}</span>
                  </div>
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t("lbl_platform_logo") || "Platform Logo"}
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl border-2 border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50 flex-shrink-0">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-1" />
                    ) : (
                      <Image className="w-6 h-6 text-gray-300" />
                    )}
                  </div>
                  <div>
                    <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 transition">
                      <Upload className="w-3.5 h-3.5" />
                      {t("btn_upload_logo") || "Upload Logo"}
                      <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("lbl_logo_desc") || "PNG or JPG, max 2MB. Recommended: 200x200px"}
                    </p>
                  </div>
                  {logoPreview && (
                    <button
                      onClick={() => { setLogoFile(null); setLogoPreview(null); setLogoRemoved(true); }}
                      className="text-xs text-red-500 hover:text-red-700">
                      {t("btn_remove_logo") || "Remove"}
                    </button>
                  )}
                </div>
              </div>

              {brandingMsg && (
                <p className={`text-xs rounded-lg px-3 py-2 ${brandingMsg.startsWith("Error") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"}`}>
                  {brandingMsg}
                </p>
              )}

              <button
                onClick={handleSaveBranding}
                disabled={savingBranding}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                {savingBranding
                  ? (t("lbl_saving") || "Saving...")
                  : <><Save className="w-4 h-4" /> {t("btn_save_branding") || "Save Branding"}</>
                }
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ── Data & Maintenance ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-base font-bold text-gray-800 mb-5 flex items-center gap-2">
          <Database className="w-5 h-5 text-gray-500" />
          {t("lbl_data_maintenance") || "Data & Maintenance"}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Auto Backup card */}
          <div className="border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Database className="w-4 h-4" />
              {t("lbl_auto_backup") || "Auto Backup"}
            </h3>

            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-gray-800">{t("lbl_enable_backup") || "Enable Automatic Backup"}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t("lbl_backup_desc") || "Export Firestore data to Google Cloud Storage on schedule"}</p>
              </div>
              <div dir="ltr">
                <UISwitch
                  checked={backupSettings.enabled}
                  onChange={v => setBackupSettings(p => ({ ...p, enabled: v }))}
                />
              </div>
            </div>

            {backupSettings.enabled && (
              <div className="space-y-4 pt-2 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_backup_frequency") || "Backup Frequency"}</label>
                  <select
                    value={backupSettings.frequency}
                    onChange={e => setBackupSettings(p => ({ ...p, frequency: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                    <option value="daily">{t("opt_daily") || "Every Day"}</option>
                    <option value="weekly">{t("opt_weekly") || "Every Week"}</option>
                    <option value="every_10_days">{t("opt_every_10") || "Every 10 Days"}</option>
                    <option value="monthly">{t("opt_monthly") || "Every Month"}</option>
                    <option value="custom">{t("opt_custom_days") || "Custom (every N days)"}</option>
                  </select>
                </div>

                {backupSettings.frequency === "custom" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_every_n_days") || "Every how many days?"}</label>
                    <input
                      type="number" min={1} max={365}
                      value={backupSettings.custom_days}
                      onChange={e => setBackupSettings(p => ({ ...p, custom_days: parseInt(e.target.value) || 1 }))}
                      className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_backup_time") || "Run backup at (hour)"}</label>
                  <select
                    value={backupSettings.time_hour}
                    onChange={e => setBackupSettings(p => ({ ...p, time_hour: parseInt(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, "0")}:00 — {i < 12 ? `${i === 0 ? 12 : i} AM` : `${i === 12 ? 12 : i - 12} PM`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_retain_backups") || "Keep backups for (days)"}</label>
                  <select
                    value={backupSettings.retain_backups}
                    onChange={e => setBackupSettings(p => ({ ...p, retain_backups: parseInt(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                    <option value={7}>{t("opt_7_days") || "7 days"}</option>
                    <option value={14}>{t("opt_14_days") || "14 days"}</option>
                    <option value={30}>{t("opt_30_days") || "30 days"}</option>
                    <option value={60}>{t("opt_60_days") || "60 days"}</option>
                    <option value={90}>{t("opt_90_days") || "90 days"}</option>
                    <option value={365}>{t("opt_1_year") || "1 year"}</option>
                  </select>
                </div>

                {backupLogs.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">{t("lbl_recent_backups") || "Recent Backups"}</p>
                    <div className="space-y-1.5">
                      {backupLogs.map(log => (
                        <div key={log.id}
                          className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${
                            log.status === "failed" ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"
                          }`}>
                          <span>{log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : "—"}</span>
                          <span className="font-medium capitalize">{log.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleRunBackupNow}
                  disabled={runningBackup}
                  className="w-full bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-medium py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5 transition">
                  {runningBackup
                    ? (t("lbl_backup_running") || "Running backup...")
                    : <><Database className="w-4 h-4" /> {t("btn_backup_now") || "Run Backup Now"}</>
                  }
                </button>
              </div>
            )}

            {backupMsg && (
              <p className={`text-xs mt-3 rounded-lg px-3 py-2 ${
                backupMsg.startsWith("Error") || backupMsg.includes("failed")
                  ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"
              }`}>{backupMsg}</p>
            )}

            <button
              onClick={handleSaveBackupSettings}
              disabled={savingBackup}
              className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {savingBackup ? (t("lbl_saving") || "Saving...") : <><Save className="w-4 h-4" /> {t("btn_save") || "Save"}</>}
            </button>
          </div>

          {/* Data Retention card */}
          <div className="border border-gray-100 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 pb-2 border-b flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              {t("lbl_data_retention") || "Data Retention"}
            </h3>
            <p className="text-xs text-gray-400 mb-4">
              {t("lbl_data_retention_desc") || "Old GPS events are automatically deleted. Client data and sales are never deleted."}
            </p>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("lbl_keep_events_for") || "Keep events and alerts for"}</label>
              <select
                value={retentionSettings.months}
                onChange={e => setRetentionSettings(p => ({ ...p, months: parseInt(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
                <option value={0}>{t("opt_keep_forever") || "Keep forever (no deletion)"}</option>
                <option value={3}>{t("opt_3_months") || "3 months"}</option>
                <option value={6}>{t("opt_6_months") || "6 months"}</option>
                <option value={12}>{t("opt_12_months") || "12 months (recommended)"}</option>
                <option value={24}>{t("opt_24_months") || "24 months"}</option>
                <option value={36}>{t("opt_36_months") || "36 months"}</option>
              </select>
            </div>

            {retentionSettings.months > 0 && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                <p className="text-yellow-700 text-xs">
                  {t("lbl_retention_warning") || "Events older than the selected period will be permanently deleted on the 1st of each month."}
                </p>
              </div>
            )}

            {retentionMsg && (
              <p className={`text-xs mt-3 rounded-lg px-3 py-2 ${
                retentionMsg.startsWith("Error") ? "bg-red-50 text-red-600" : "bg-green-50 text-green-600"
              }`}>{retentionMsg}</p>
            )}

            <button
              onClick={handleSaveRetention}
              disabled={savingRetention}
              className="mt-3 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {savingRetention ? (t("lbl_saving") || "Saving...") : <><Save className="w-4 h-4" /> {t("btn_save") || "Save"}</>}
            </button>
          </div>

        </div>
      </div>

      {/* Connection History — full width */}
      <SessionHistoryPanel collectionPath={["platform", "super_admin_sessions", "sessions"]} showRole={false} />

    </div>
  );
}

export default SuperAdminDashboard;
